// Main sale view. F3: payment methods, "Si paga con..." preview,
// automatic configured discounts, "cuánto entró realmente" sheet,
// success modal with access to the detail, persistent last-sale chip.
//
// Phase B1 refactor: header rebuilt with PageHeader + IconButtons, grid
// with ProductTile, panel payment buttons with PaymentMethodButton,
// category chips with the system Chip. Sheets and the movement flow
// stay untouched in this phase.

import { useEffect, useMemo, useRef, useState } from "react";
import { useCategories, useProducts } from "../products/hooks";
import {
  useSaleDraft,
  selectItemCount,
  selectItemSubtotalCents,
  selectGlobalDiscountCents,
  type SaleDraftItem,
} from "./draft-store";
import { Input } from "../../components/Input";
import { MoneyInput } from "../../components/MoneyInput";
import { MoneyConfirmationPanel } from "../../components/MoneyConfirmationPanel";
import { Sheet } from "../../components/Sheet";
import { Button } from "../../components/Button";
import { Chip } from "../../components/Chip";
import { Toast } from "../../components/Toast";
import { IconButton } from "../../components/IconButton";
import { PageHeader } from "../../components/PageHeader";
import { ProductTile } from "../../components/ProductTile";
import {
  AllCategoriesGlyph,
  CategoryGlyph,
  categoryAccentClassName,
} from "../../components/CatalogVisuals";
import { PaymentMethodButton } from "../../components/PaymentMethodButton";
import { CheckIcon, ConsignmentIcon, LogOutIcon, MoneyCircleIcon, ReceiptIcon, ShoppingBagIcon, SplitPaymentIcon, TrashIcon } from "../../components/icons";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { formatARS, getArsAmountPolicy, inputValueFromCents, parseArsInput } from "../../lib/money";
import { ApiError } from "../../lib/api";
import type { Product } from "../products/api";
import type { ProductVisualKey } from "@ventas/shared";
import { useSaleDraftItemsByCategory } from "./selectors";
import {
  usePaymentMethodsForSale,
  useCreateSale,
  useSale,
  useSales,
} from "../sales/hooks";
import { previewByMethod } from "../sales/preview";
import type { PaymentMethod } from "../payment-methods/api";
import type { SplitPaymentInput } from "../sales/api";
import type { ConsignmentPaymentInput } from "../consignments/api";
import {
  useConsignment,
  useConvertConsignment,
  useCreateConsignment,
  useRefundConsignment,
  useUpdateConsignment,
} from "../consignments/hooks";
import { uuid } from "../../lib/uuid";
import { useCustomers, useCreateCustomer, fullName } from "../customers/hooks";
import type { CustomerListEntry } from "../customers/api";
import {
  useCreateMovement,
  useCreateMovementConcept,
  useMovementConcepts,
  useRecentConcepts,
} from "../movements/hooks";
import {
  PRODUCT_SORT_LABELS,
  sortProducts,
  type ProductSortMode,
} from "./product-sort";

const LAST_SALE_KEY = "ventas.lastSaleId";
const CONSIGNMENT_FEEDBACK_KEY = "ventas.consignmentFeedback";

type PendingMoneyConfirmation = {
  token: string;
  amountCents: number;
  actionLabel: string;
  /** Retries the immutable original request with the token the API issued. */
  retry: (token: string) => Promise<void>;
};

export function SaleView() {
  const location = useLocation();
  const navigate = useNavigate();
  const { id: consignmentParam } = useParams<{ id: string }>();
  const editingConsignmentId = consignmentParam ? Number(consignmentParam) : null;
  const isEditingConsignment = Number.isInteger(editingConsignmentId) && editingConsignmentId! > 0;
  const [consignmentMode, setConsignmentMode] = useState(isEditingConsignment);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState<number | null>(null);
  const [productSort, setProductSort] = useState<ProductSortMode>("best-selling");
  const [sortMenuOpen, setSortMenuOpen] = useState(false);
  const [priceSheetFor, setPriceSheetFor] = useState<Product | null>(null);
  const [netSheetFor, setNetSheetFor] = useState<PaymentMethod | null>(null);
  const [salePanelOpen, setSalePanelOpen] = useState(false);
  const [success, setSuccess] = useState<{
    saleId: number;
    total: number;
    methodName: string;
  } | null>(null);
  const [lastSaleId, setLastSaleId] = useState<number | null>(() => {
    const raw = localStorage.getItem(LAST_SALE_KEY);
    return raw ? Number(raw) : null;
  });
  // cta cte flow: opens ChooseCustomerSheet when the operator taps the
  // 'Cuenta corriente' payment method, then holds the chosen customer
  // id in the draft store until confirm.
  const [chooseCustomerFor, setChooseCustomerFor] =
    useState<PaymentMethod | null>(null);
  const customerId = useSaleDraft((s) => s.customerId);
  const setCustomer = useSaleDraft((s) => s.setCustomer);
  const setItemNote = useSaleDraft((s) => s.setItemNote);
  const draftItems = useSaleDraft((s) => s.items);
  // Per-line observations are available for every sale and consignment.
  const [noteForLocalId, setNoteForLocalId] = useState<string | null>(null);
  // Manual movement flow: first sheet picks the type, second sheet
  // collects the rest. We keep them as separate states so closing one
  // sheet always returns to the type picker or to the caja, never to
  // a half-filled form.
  const [movementTypePickerOpen, setMovementTypePickerOpen] = useState(false);
  const [movementSheetType, setMovementSheetType] = useState<
    "income" | "expense" | null
  >(null);
  const [globalDiscountSheetOpen, setGlobalDiscountSheetOpen] = useState(false);
  const [splitPaymentSheetOpen, setSplitPaymentSheetOpen] = useState(false);
  const [customerPickerOpen, setCustomerPickerOpen] = useState(false);
  const [customerPickerFromSalePanel, setCustomerPickerFromSalePanel] = useState(false);
  const [advanceMethodFor, setAdvanceMethodFor] = useState<PaymentMethod | null>(null);
  const [settlementMethodFor, setSettlementMethodFor] = useState<PaymentMethod | null>(null);
  const [convertingMethodId, setConvertingMethodId] = useState<number | undefined>();
  const [pendingConsignmentPayments, setPendingConsignmentPayments] = useState<ConsignmentPaymentInput[]>([]);
  const [consignmentFeedback, setConsignmentFeedback] = useState<{
    kind: "success" | "error";
    message: string;
  } | null>(null);
  const [pendingMoneyConfirmation, setPendingMoneyConfirmation] = useState<PendingMoneyConfirmation | null>(null);
  const [moneyConfirmationError, setMoneyConfirmationError] = useState<string | null>(null);
  const [confirmingMoney, setConfirmingMoney] = useState(false);
  const productsQ = useProducts(search || undefined, true);
  const categoriesQ = useCategories();
  const paymentMethodsQ = usePaymentMethodsForSale();
  const latestSalesQ = useSales({ limit: 1 });
  const effectiveLastSaleId = lastSaleId ?? latestSalesQ.data?.[0]?.id ?? null;
  const lastSaleQ = useSale(effectiveLastSaleId, effectiveLastSaleId != null);
  const createSale = useCreateSale();
  const consignmentQ = useConsignment(isEditingConsignment ? editingConsignmentId : null);
  const createConsignment = useCreateConsignment();
  const updateConsignment = useUpdateConsignment();
  const convertConsignment = useConvertConsignment();
  const refundConsignment = useRefundConsignment();
  // All active customers (no search). Used to render the cta cte chip in
  // the sale panel once one is selected. We keep a separate hook for the
  // ChooseCustomerSheet which uses the search query.
  const allCustomersQ = useCustomers({ withBalance: false, limit: 200 });
  const createCustomer = useCreateCustomer();
  const hydrateDraft = useSaleDraft((s) => s.hydrate);
  const openedConsignmentPanelRef = useRef<number | null>(null);

  useEffect(() => {
    const routeFeedback = (location.state as { consignmentFeedback?: string } | null)?.consignmentFeedback;
    const pendingFeedback = sessionStorage.getItem(CONSIGNMENT_FEEDBACK_KEY);
    const message = routeFeedback ?? pendingFeedback;
    if (!message) return;
    setConsignmentFeedback({ kind: "success", message });
    if (pendingFeedback) sessionStorage.removeItem(CONSIGNMENT_FEEDBACK_KEY);
    if (routeFeedback) window.history.replaceState(null, "", window.location.href);
  }, [location.state]);

  useEffect(() => {
    const consignment = consignmentQ.data;
    if (!consignment) return;
    hydrateDraft({
      customerId: consignment.customerId,
      globalDiscountPct:
        consignment.itemsSubtotalCents > 0
          ? Math.round((consignment.globalDiscountCents / consignment.itemsSubtotalCents) * 100)
          : 0,
      items: consignment.items.map((item) => ({
        localId: `consignment_${item.id}`,
        productId: item.productId,
        productNameSnapshot: item.productName,
        unitPriceCents: item.unitPriceCents,
        qty: item.qty,
        lineDiscountCents: item.qty > 0 ? Math.round(item.lineDiscountCents / item.qty) : 0,
        sizeGuideId: item.sizeGuideId ?? null,
        sizeGuideNameSnapshot: item.sizeGuideName,
        sizeSnapshot: item.size,
        note: item.note,
      })),
    });
  }, [consignmentQ.data, hydrateDraft]);

  useEffect(() => {
    const consignment = consignmentQ.data;
    if (!isEditingConsignment || !consignment || openedConsignmentPanelRef.current === consignment.id) return;
    openedConsignmentPanelRef.current = consignment.id;
    setSalePanelOpen(true);
  }, [consignmentQ.data, isEditingConsignment]);

  const visible = useMemo(() => {
    const list = productsQ.data ?? [];
    const filtered = category ? list.filter((p) => p.categoryId === category) : list;
    return sortProducts(filtered, productSort);
  }, [productsQ.data, category, productSort]);

  const items = useSaleDraftItemsByCategory();
  const itemsList = useSaleDraft((s) => s.items);
  const itemsSubtotalCents = useSaleDraft(selectItemSubtotalCents);
  const globalDiscountPct = useSaleDraft((s) => s.globalDiscountPct);
  const globalDiscountCents = useSaleDraft(selectGlobalDiscountCents);
  const totalBeforePaymentMethodCents = Math.max(0, itemsSubtotalCents - globalDiscountCents);
  const clearDraft = useSaleDraft((s) => s.clear);
  const isConsignment = consignmentMode || isEditingConsignment;
  const exitConsignmentEditing = () => {
    clearDraft();
    setPendingConsignmentPayments([]);
    setConsignmentMode(false);
    openedConsignmentPanelRef.current = null;
    navigate("/");
  };
  const selectedCustomer = customerId != null
    ? allCustomersQ.data?.find((customer) => customer.id === customerId) ?? null
    : null;
  const consignmentPaidCents = (consignmentQ.data?.totalPaidCents ?? 0)
    + pendingConsignmentPayments.reduce((sum, payment) => sum + payment.amountCents, 0);
  const consignmentRefundedCents = consignmentQ.data?.totalRefundedCents ?? 0;
  const consignmentBalanceCents = Math.max(
    0,
    totalBeforePaymentMethodCents - consignmentPaidCents + consignmentRefundedCents
  );

  const openCustomerPicker = (fromSalePanel = false) => {
    setCustomerPickerFromSalePanel(fromSalePanel);
    if (fromSalePanel) setSalePanelOpen(false);
    setCustomerPickerOpen(true);
  };

  const closeCustomerPicker = (reopenSalePanel = customerPickerFromSalePanel) => {
    setCustomerPickerOpen(false);
    setCustomerPickerFromSalePanel(false);
    if (reopenSalePanel) setSalePanelOpen(true);
  };

  const consignmentErrorMessage = (error: unknown) => {
    if (error instanceof ApiError && error.body && typeof error.body === "object") {
      const code = (error.body as { error?: unknown }).error;
      if (code === "payments_exceed_total") return "Los anticipos no pueden superar el total de la consignación.";
      if (code === "invalid_customer") return "Elegí un cliente válido para guardar la consignación.";
      if (code === "consignment_not_open") return "La consignación ya no está abierta para editarla.";
      if (code === "refund_required") return "El nuevo total requiere registrar una devolución antes de continuar.";
    }
    return "No se pudo guardar la consignación. Intentá nuevamente.";
  };

  const queueMoneyConfirmation = (
    error: unknown,
    fallbackAmountCents: number,
    actionLabel: string,
    retry: (token: string) => Promise<void>
  ) => {
    const confirmation = getMoneyConfirmation(error);
    if (!confirmation) return false;
    setMoneyConfirmationError(null);
    setPendingMoneyConfirmation({
      token: confirmation.token,
      amountCents: confirmation.amountCents ?? fallbackAmountCents,
      actionLabel,
      retry,
    });
    return true;
  };

  const confirmUnusualMoney = async () => {
    const current = pendingMoneyConfirmation;
    if (!current) return;
    setMoneyConfirmationError(null);
    setConfirmingMoney(true);
    try {
      await current.retry(current.token);
      setPendingMoneyConfirmation(null);
    } catch (error) {
      const confirmation = getMoneyConfirmation(error);
      if (confirmation) {
        setPendingMoneyConfirmation((pending) => pending && {
          ...pending,
          token: confirmation.token,
          amountCents: confirmation.amountCents ?? pending.amountCents,
        });
      } else {
        setMoneyConfirmationError((error as Error).message || "No se pudo confirmar el importe.");
      }
    } finally {
      setConfirmingMoney(false);
    }
  };

  const previews = useMemo(
    () =>
      previewByMethod(itemsList, globalDiscountCents, paymentMethodsQ.data ?? []),
    [itemsList, globalDiscountCents, paymentMethodsQ.data]
  );

  const onPickMethod = (pm: PaymentMethod) => {
    if (isConsignment) {
      if (isEditingConsignment) void settleEditedConsignment(pm);
      else if (!pm.requiresCustomer) setAdvanceMethodFor(pm);
      return;
    }
    if (pm.requiresCustomer) {
      // Cta cte: must select a customer first. If one is already chosen
      // from a previous tap, submit straight away.
      if (customerId != null) {
        if (pm.requiresNetReceived) setNetSheetFor(pm);
        else void submitSale(pm);
      } else {
        setChooseCustomerFor(pm);
      }
    } else {
      if (pm.requiresNetReceived) {
        setNetSheetFor(pm);
      } else {
        void submitSale(pm);
      }
    }
  };

  const draftItemsForApi = (items: SaleDraftItem[]) => items.map((it) => ({
    productId: it.productId,
    productName: it.productNameSnapshot,
    unitPriceCents: it.unitPriceCents,
    qty: it.qty,
    lineDiscountCents: it.lineDiscountCents * it.qty,
    sizeGuideId: it.sizeGuideId,
    size: it.sizeSnapshot,
    note: it.note,
  }));

  const refundExcessAfterConfirmation = async (
    error: unknown,
    afterConfirmedRefund?: () => Promise<void>
  ): Promise<"refunded" | "confirmation_pending" | "not_handled"> => {
    if (!(error instanceof ApiError) || !error.body || typeof error.body !== "object") return "not_handled";
    const body = error.body as { error?: unknown; excessCents?: unknown };
    if (body.error !== "refund_required" || typeof body.excessCents !== "number" || body.excessCents <= 0) return "not_handled";
    const consignment = consignmentQ.data;
    if (!consignment) return "not_handled";

    let remaining = body.excessCents;
    const allocations: Array<{ paymentId: number; paymentMethodId: number; amountCents: number }> = [];
    for (const payment of consignment.payments) {
      const alreadyRefunded = consignment.refunds
        .filter((refund) => refund.paymentId === payment.id)
        .reduce((sum, refund) => sum + refund.amountCents, 0);
      const available = Math.max(0, payment.amountCents - alreadyRefunded);
      const amount = Math.min(remaining, available);
      if (amount > 0) {
        allocations.push({ paymentId: payment.id, paymentMethodId: payment.paymentMethodId, amountCents: amount });
        remaining -= amount;
      }
      if (remaining === 0) break;
    }
    if (remaining > 0 || allocations.length === 0) return "not_handled";
    if (!window.confirm(`El nuevo total es menor que lo pagado. Se devolverán ${formatARS(body.excessCents)} por los medios originales. ¿Continuar?`)) return "not_handled";
    const refundBody = {
      allocations,
      reason: "Devolución por productos devueltos",
    };
    try {
      await refundConsignment.mutateAsync({
      id: consignment.id,
        body: refundBody,
      });
    } catch (refundError) {
      if (queueMoneyConfirmation(refundError, body.excessCents, "Confirmar devolución", async (token) => {
        await refundConsignment.mutateAsync({
          id: consignment.id,
          body: { ...refundBody, moneyConfirmationToken: token },
        });
        await afterConfirmedRefund?.();
      })) return "confirmation_pending";
      throw refundError;
    }
    return "refunded";
  };

  const saveConsignment = async () => {
    const draftState = useSaleDraft.getState();
    if (draftState.items.length === 0 || draftState.customerId == null) {
      if (draftState.customerId == null) openCustomerPicker(true);
      return;
    }
    try {
      const items = draftItemsForApi(draftState.items);
      if (isEditingConsignment && editingConsignmentId) {
        const body = {
          customerId: draftState.customerId,
          globalDiscountCents: selectGlobalDiscountCents(draftState),
          items,
          newPayments: pendingConsignmentPayments,
        };
        try {
          await updateConsignment.mutateAsync({ id: editingConsignmentId, body });
        } catch (error) {
          if (queueMoneyConfirmation(error, totalBeforePaymentMethodCents, "Confirmar cambios de consignación", async (token) => {
            await updateConsignment.mutateAsync({ id: editingConsignmentId, body: { ...body, moneyConfirmationToken: token } });
            setPendingConsignmentPayments([]);
            setConsignmentFeedback({ kind: "success", message: "Consignación actualizada." });
          })) return;
          const refund = await refundExcessAfterConfirmation(error, async () => {
            await updateConsignment.mutateAsync({ id: editingConsignmentId, body });
            setPendingConsignmentPayments([]);
            setConsignmentFeedback({ kind: "success", message: "Consignación actualizada." });
          });
          if (refund === "confirmation_pending") return;
          if (refund !== "refunded") throw error;
          await updateConsignment.mutateAsync({ id: editingConsignmentId, body });
        }
        setPendingConsignmentPayments([]);
        setConsignmentFeedback({ kind: "success", message: "Consignación actualizada." });
        return;
      }
      const body = {
        clientId: uuid(),
        customerId: draftState.customerId,
        globalDiscountCents: selectGlobalDiscountCents(draftState),
        items,
        payments: pendingConsignmentPayments,
      };
      const finish = (created: Awaited<ReturnType<typeof createConsignment.mutateAsync>>) => {
        clearDraft();
        setPendingConsignmentPayments([]);
        setConsignmentMode(false);
        sessionStorage.setItem(CONSIGNMENT_FEEDBACK_KEY, "Consignación guardada.");
        navigate(`/consignaciones/${created.id}/editar`);
      };
      try {
        finish(await createConsignment.mutateAsync(body));
      } catch (error) {
        const highestAmount = Math.max(
          totalBeforePaymentMethodCents,
          ...pendingConsignmentPayments.map((payment) => payment.amountCents)
        );
        if (queueMoneyConfirmation(error, highestAmount, "Confirmar consignación", async (token) => {
          finish(await createConsignment.mutateAsync({ ...body, moneyConfirmationToken: token }));
        })) return;
        throw error;
      }
    } catch (error) {
      setConsignmentFeedback({ kind: "error", message: consignmentErrorMessage(error) });
    }
  };

  const convertCurrentConsignment = async (payment: ConsignmentPaymentInput & { accountCurrent?: boolean } | null) => {
    if (!editingConsignmentId) return;
    try {
      const draftState = useSaleDraft.getState();
      if (draftState.customerId == null) {
        openCustomerPicker(true);
        return;
      }
      const updateBody = {
        customerId: draftState.customerId,
        globalDiscountCents: selectGlobalDiscountCents(draftState),
        items: draftItemsForApi(draftState.items),
        newPayments: pendingConsignmentPayments,
      };
      try {
        await updateConsignment.mutateAsync({ id: editingConsignmentId, body: updateBody });
      } catch (error) {
        if (queueMoneyConfirmation(error, totalBeforePaymentMethodCents, "Confirmar cambios de consignación", async (token) => {
          await updateConsignment.mutateAsync({ id: editingConsignmentId, body: { ...updateBody, moneyConfirmationToken: token } });
          setPendingConsignmentPayments([]);
          setSettlementMethodFor(null);
          setSalePanelOpen(true);
          setConsignmentFeedback({ kind: "success", message: "Cambios guardados. Confirmá la conversión nuevamente." });
        })) return;
        const refund = await refundExcessAfterConfirmation(error, async () => {
          await updateConsignment.mutateAsync({ id: editingConsignmentId, body: updateBody });
          setPendingConsignmentPayments([]);
          setSettlementMethodFor(null);
          setSalePanelOpen(true);
          setConsignmentFeedback({ kind: "success", message: "Devolución registrada y cambios guardados. Confirmá la conversión nuevamente." });
        });
        if (refund === "confirmation_pending") return;
        if (refund !== "refunded") throw error;
        await updateConsignment.mutateAsync({ id: editingConsignmentId, body: updateBody });
      }
      setPendingConsignmentPayments([]);
      const convertBody = {
        items: draftItemsForApi(draftState.items),
        globalDiscountCents: selectGlobalDiscountCents(draftState),
        payment,
      };
      try {
        await convertConsignment.mutateAsync({ id: editingConsignmentId, body: convertBody });
      } catch (error) {
        const highestAmount = Math.max(
          totalBeforePaymentMethodCents,
          payment?.amountCents ?? 0,
          payment?.netReceivedCents ?? 0
        );
        if (queueMoneyConfirmation(error, highestAmount, "Confirmar conversión", async (token) => {
          await convertConsignment.mutateAsync({ id: editingConsignmentId, body: { ...convertBody, moneyConfirmationToken: token } });
          setSettlementMethodFor(null);
          clearDraft();
          sessionStorage.setItem(CONSIGNMENT_FEEDBACK_KEY, "Consignación convertida en venta.");
          navigate("/consignaciones");
        })) return;
        const refund = await refundExcessAfterConfirmation(error, async () => {
          setSettlementMethodFor(null);
          setSalePanelOpen(true);
          setConsignmentFeedback({ kind: "success", message: "Devolución registrada. Confirmá la conversión nuevamente." });
        });
        if (refund === "confirmation_pending") return;
        if (refund !== "refunded") throw error;
        await convertConsignment.mutateAsync({ id: editingConsignmentId, body: convertBody });
      }
      setSettlementMethodFor(null);
      clearDraft();
      sessionStorage.setItem(CONSIGNMENT_FEEDBACK_KEY, "Consignación convertida en venta.");
      navigate("/consignaciones");
    } catch (error) {
      setSettlementMethodFor(null);
      setSalePanelOpen(true);
      setConsignmentFeedback({ kind: "error", message: consignmentErrorMessage(error) });
    }
  };

  const settleEditedConsignment = async (method: PaymentMethod) => {
    if (customerId == null) {
      openCustomerPicker(true);
      return;
    }
    if (consignmentBalanceCents <= 0) {
      await convertCurrentConsignment(null);
      return;
    }
    if (method.requiresNetReceived) {
      setSettlementMethodFor(method);
      return;
    }
    setConvertingMethodId(method.id);
    try {
      await convertCurrentConsignment({
        paymentMethodId: method.id,
        amountCents: consignmentBalanceCents,
        ...(method.requiresCustomer ? { accountCurrent: true } : {}),
      });
    } finally {
      setConvertingMethodId(undefined);
    }
  };

  const submitSale = async (
    pm: PaymentMethod,
    opts: { netReceivedOverride?: number; skipNetReceived?: boolean } = {}
  ) => {
    const draftState = useSaleDraft.getState();
    const items = draftState.items;
    if (items.length === 0) return;
    const body = {
      clientId: uuid(),
      paymentMethodId: pm.id,
      globalDiscountCents: selectGlobalDiscountCents(draftState),
      customerId: draftState.customerId,
      items: items.map((it) => ({
        productId: it.productId,
        productName: it.productNameSnapshot,
        unitPriceCents: it.unitPriceCents,
        qty: it.qty,
        lineDiscountCents: it.lineDiscountCents * it.qty,
        sizeGuideId: it.sizeGuideId,
        size: it.sizeSnapshot,
        note: it.note,
      })),
      ...(opts.netReceivedOverride !== undefined
        ? { netReceivedOverride: opts.netReceivedOverride }
        : {}),
      ...(opts.skipNetReceived ? { skipNetReceived: true } : {}),
    };
    const finish = (sale: Awaited<ReturnType<typeof createSale.mutateAsync>>) => {
      clearDraft();
      setLastSaleId(sale.id);
      localStorage.setItem(LAST_SALE_KEY, String(sale.id));
      setSuccess({ saleId: sale.id, total: sale.totalChargedCents, methodName: pm.name });
      setNetSheetFor(null);
      setChooseCustomerFor(null);
      setSalePanelOpen(false);
    };
    try {
      finish(await createSale.mutateAsync(body));
    } catch (error) {
      if (queueMoneyConfirmation(error, totalBeforePaymentMethodCents, "Confirmar venta", async (token) => {
        finish(await createSale.mutateAsync({ ...body, moneyConfirmationToken: token }));
      })) return;
      throw error;
    }
  };

  const submitSplitSale = async (payments: SplitPaymentInput[]) => {
    const draftState = useSaleDraft.getState();
    if (draftState.items.length === 0 || payments.length < 2) return;
    const body = {
      clientId: uuid(),
      paymentMethodId: payments[0].paymentMethodId,
      globalDiscountCents: selectGlobalDiscountCents(draftState),
      customerId: draftState.customerId,
      payments,
      items: draftState.items.map((item) => ({
        productId: item.productId,
        productName: item.productNameSnapshot,
        unitPriceCents: item.unitPriceCents,
        qty: item.qty,
        lineDiscountCents: item.lineDiscountCents * item.qty,
        sizeGuideId: item.sizeGuideId,
        size: item.sizeSnapshot,
        note: item.note,
      })),
    };
    const finish = (sale: Awaited<ReturnType<typeof createSale.mutateAsync>>) => {
      clearDraft();
      setLastSaleId(sale.id);
      localStorage.setItem(LAST_SALE_KEY, String(sale.id));
      setSuccess({ saleId: sale.id, total: sale.totalChargedCents, methodName: "Pago dividido" });
      setSplitPaymentSheetOpen(false);
    };
    try {
      finish(await createSale.mutateAsync(body));
    } catch (error) {
      const largestPayment = payments.reduce((largest, payment) => Math.max(largest, payment.amountCents), 0);
      if (queueMoneyConfirmation(error, largestPayment || totalBeforePaymentMethodCents, "Confirmar pago dividido", async (token) => {
        finish(await createSale.mutateAsync({ ...body, moneyConfirmationToken: token }));
      })) return;
      throw error;
    }
  };

  const headerActionsProps = {
    onMovement: () => setMovementTypePickerOpen(true),
    onVerUltima: () => {
      if (effectiveLastSaleId) navigate(`/sales/${effectiveLastSaleId}`, { state: { from: "/" } });
    },
    lastSale: lastSaleQ.data ? { status: lastSaleQ.data.status } : null,
  };
  // Desktop (PageHeader): default wrap, plenty of horizontal room.
  const headerActions = <SaleHeaderActions {...headerActionsProps} />;
  // Mobile (next to the category carousel): must stay a single line so the
  // toolbar doesn't grow in height when both buttons are present.
  const headerActionsCompact = <SaleHeaderActions {...headerActionsProps} wrap={false} />;

  return (
    <main className="page sale-page vl-sale-main">
      {/* Hidden on mobile via CSS (.sale-page-header): the active "Venta" tab
          in the bottom nav already identifies this screen, so the title bar
          is redundant there. Its non-duplicated actions (Registrar
          movimiento / Última venta / Consignaciones — none exists elsewhere in the
          sticky toolbar) move next to the category carousel inside
          .sale-catalog-tools__category-row below, which mirrors the same
          breakpoint, so they don't add an extra vertical band on mobile. */}
      <div className="sale-page-header">
        <PageHeader
          title={isEditingConsignment ? "Editar consignación" : "Nueva venta"}
          onBack={isEditingConsignment ? exitConsignmentEditing : undefined}
          backLabel="Salir de edición"
          actions={headerActions}
        />
      </div>

      <div className="sale-catalog-tools">
        <div className="sale-catalog-tools__search-row">
          <div role="search">
            <SearchField
              value={search}
              onChange={setSearch}
              placeholder="Buscar producto..."
              aria-label="Buscar producto"
            />
          </div>
          <div className="catalog-sort">
            <button
              type="button"
              className="catalog-sort__trigger"
              aria-label={`Ordenar productos: ${PRODUCT_SORT_LABELS[productSort]}`}
              title={`Ordenar: ${PRODUCT_SORT_LABELS[productSort]}`}
              aria-expanded={sortMenuOpen}
              onClick={() => setSortMenuOpen((open) => !open)}
            >
              <ArrowDownUpIcon />
            </button>
            {sortMenuOpen && (
              <div className="catalog-sort__menu" role="menu" aria-label="Orden de productos">
                {(Object.keys(PRODUCT_SORT_LABELS) as ProductSortMode[]).map((mode) => (
                  <button
                    type="button"
                    role="menuitemradio"
                    aria-checked={productSort === mode}
                    key={mode}
                    onClick={() => {
                      setProductSort(mode);
                      setSortMenuOpen(false);
                    }}
                  >
                    <span>{PRODUCT_SORT_LABELS[mode]}</span>
                    {productSort === mode && <CheckIcon />}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="sale-catalog-tools__category-row">
          <CategoryChips
            categories={categoriesQ.data ?? []}
            selected={category}
            onSelect={setCategory}
          />
          <div className="sale-catalog-tools__actions-row">{headerActionsCompact}</div>
        </div>
      </div>

      <div className={`sale-consignment-toolbar${isConsignment ? " is-active" : ""}${isEditingConsignment ? " is-editing" : ""}`}>
        <label className="sale-consignment-switch">
          <input
            type="checkbox"
            checked={isConsignment}
            disabled={isEditingConsignment}
            onChange={(event) => {
              setConsignmentMode(event.target.checked);
              if (!event.target.checked) {
                setPendingConsignmentPayments([]);
                setCustomer(null);
              }
            }}
          />
          <span>
            <strong>Modo consignación</strong>
          </span>
        </label>
        {isEditingConsignment && (
          <IconButton
            icon={<LogOutIcon />}
            label="Salir de edición"
            text="Salir de edición"
            variant="danger"
            size="md"
            className="sale-consignment-action sale-consignment-action--exit"
            style={{ width: 44, height: 44, flex: "0 0 44px" }}
            onClick={exitConsignmentEditing}
          />
        )}
        <IconButton
          icon={<UserIcon />}
          label={selectedCustomer ? `Cambiar cliente: ${fullName(selectedCustomer)}` : "Elegir cliente"}
          text={selectedCustomer ? fullName(selectedCustomer) : <><span className="sale-consignment-customer__desktop-label">Elegir cliente</span><span className="sale-consignment-customer__mobile-label">Elegir</span></>}
          variant={selectedCustomer ? "solid" : isConsignment ? "danger" : "outline"}
          size="md"
          className="sale-consignment-action sale-consignment-action--customer"
          style={{ width: 44, height: 44, flex: "0 0 44px" }}
          onClick={() => openCustomerPicker(false)}
        />
      </div>

      <div className="sale-workspace">
        <section className="sale-catalog" aria-label="Catálogo de productos">
          {productsQ.isLoading && <div className="product-skeleton-grid"><span /><span /><span /><span /><span /><span /></div>}
          {!productsQ.isLoading && visible.length === 0 && (
            <div className="empty-state sale-empty"><strong>{search ? "No encontramos productos" : "No hay productos disponibles"}</strong><span>{search ? "Probá con otra búsqueda o categoría." : "Activá productos desde la administración."}</span></div>
          )}
          <div className="vl-product-grid">
            {visible.map((p) => {
              const inCart = items.find((i) => i.productId === p.id);
              return (
                <ProductTile
                  key={p.id}
                  name={p.name}
                  category={p.category ?? undefined}
                  categoryId={p.categoryId}
                  visualKey={p.visualKey}
                  suggestedPriceCents={undefined}
                  linesCount={inCart?.linesCount ?? 0}
                  onAdd={() => setPriceSheetFor(p)}
                />
              );
            })}
          </div>
        </section>

        <SalePanel
          subtotalCents={itemsSubtotalCents}
          globalDiscountCents={globalDiscountCents}
          globalDiscountPct={globalDiscountPct}
          previews={previews}
          onPickMethod={onPickMethod}
          paymentMethods={paymentMethodsQ.data ?? []}
          confirmingId={convertingMethodId ?? netSheetFor?.id}
          loading={createSale.isPending || createConsignment.isPending || updateConsignment.isPending || convertConsignment.isPending}
          selectedCustomer={selectedCustomer ? { id: selectedCustomer.id, name: fullName(selectedCustomer) } : null}
          consignmentMode={isConsignment}
          consignmentEditing={isEditingConsignment}
          consignmentPaidCents={consignmentPaidCents}
          consignmentRefundedCents={consignmentRefundedCents}
          onChooseCustomer={() => openCustomerPicker(true)}
          onSaveConsignment={() => void saveConsignment()}
          onFinalizeConsignment={() => void convertCurrentConsignment(null)}
          onEditNote={(localId) => setNoteForLocalId(localId)}
          onEditGlobalDiscount={() => setGlobalDiscountSheetOpen(true)}
          onSplitPayment={() => setSplitPaymentSheetOpen(true)}
          open={salePanelOpen}
          onOpenChange={setSalePanelOpen}
        />
      </div>

      <PriceSheet product={priceSheetFor} onClose={() => setPriceSheetFor(null)} />

      {netSheetFor && (
        <NetReceivedSheet
          method={netSheetFor}
          onClose={() => {
            setNetSheetFor(null);
            setSalePanelOpen(true);
          }}
          onSubmit={async (mode, value) => {
            const pm = netSheetFor;
            if (mode === "confirm") {
              await submitSale(pm, {
                netReceivedOverride: value,
              });
            } else {
              await submitSale(pm, {
                skipNetReceived: true,
              });
            }
          }}
        />
      )}

      <GlobalDiscountSheet
        open={globalDiscountSheetOpen}
        subtotalCents={itemsSubtotalCents}
        currentPct={globalDiscountPct}
        onClose={() => {
          setGlobalDiscountSheetOpen(false);
          setSalePanelOpen(true);
        }}
      />

      <SplitPaymentSheet
        open={splitPaymentSheetOpen}
        totalCents={totalBeforePaymentMethodCents}
        paymentMethods={paymentMethodsQ.data ?? []}
        customers={allCustomersQ.data ?? []}
        selectedCustomerId={customerId}
        onSelectCustomer={setCustomer}
        createCustomer={createCustomer}
        submitting={createSale.isPending}
        onClose={() => {
          setSplitPaymentSheetOpen(false);
          setSalePanelOpen(true);
        }}
        onSubmit={submitSplitSale}
      />

      {customerPickerOpen && (
        <ChooseCustomerSheet
          open
          onClose={() => closeCustomerPicker()}
          onPick={(id) => {
            setCustomer(id);
            closeCustomerPicker();
          }}
          createCustomer={createCustomer}
        />
      )}

      {advanceMethodFor && (
        <ConsignmentPaymentSheet
          method={advanceMethodFor}
          remainingCents={Math.max(0, (itemsSubtotalCents - globalDiscountCents) - ((consignmentQ.data?.totalPaidCents ?? 0) - (consignmentQ.data?.totalRefundedCents ?? 0) + pendingConsignmentPayments.reduce((sum, payment) => sum + payment.amountCents, 0)))}
          onClose={() => setAdvanceMethodFor(null)}
          onSubmit={(payment) => {
            setPendingConsignmentPayments((current) => [...current, payment]);
            setAdvanceMethodFor(null);
          }}
        />
      )}

      {settlementMethodFor && isEditingConsignment && (
        <ConsignmentSettlementSheet
          method={settlementMethodFor}
          remainingCents={consignmentBalanceCents}
          pending={convertConsignment.isPending || updateConsignment.isPending}
          onClose={() => {
            setSettlementMethodFor(null);
            setSalePanelOpen(true);
          }}
          onSubmit={async (payment) => {
            setConvertingMethodId(settlementMethodFor.id);
            try {
              await convertCurrentConsignment(payment);
            } finally {
              setConvertingMethodId(undefined);
            }
          }}
        />
      )}

      {pendingMoneyConfirmation && (
        <Sheet
          open
          onClose={() => !confirmingMoney && setPendingMoneyConfirmation(null)}
          title="Confirmar importe inusual"
        >
          <MoneyConfirmationPanel
            amountCents={pendingMoneyConfirmation.amountCents}
            actionLabel={pendingMoneyConfirmation.actionLabel}
            pending={confirmingMoney}
            onConfirm={() => void confirmUnusualMoney()}
            onCancel={() => setPendingMoneyConfirmation(null)}
          />
          {moneyConfirmationError && <p className="form-error">{moneyConfirmationError}</p>}
        </Sheet>
      )}

      {success && (
        <SuccessModal
          total={success.total}
          methodName={success.methodName}
          onVerDetalle={() => navigate(`/sales/${success.saleId}`, { state: { from: "/" } })}
          onCerrar={() => setSuccess(null)}
        />
      )}

      {chooseCustomerFor && (
        <ChooseCustomerSheet
          open={true}
          onClose={() => {
            setChooseCustomerFor(null);
            setSalePanelOpen(true);
          }}
          onPick={(id) => {
            const pm = chooseCustomerFor;
            setCustomer(id);
            setChooseCustomerFor(null);
            if (pm.requiresNetReceived) setNetSheetFor(pm);
            else void submitSale(pm);
          }}
          createCustomer={createCustomer}
        />
      )}

      {noteForLocalId && (
        <LineNoteSheet
          open={true}
          item={draftItems.find((it) => it.localId === noteForLocalId) ?? null}
          onClose={() => setNoteForLocalId(null)}
          onSave={(note) => {
            setItemNote(noteForLocalId, note);
            setNoteForLocalId(null);
          }}
        />
      )}

      {movementTypePickerOpen && (
        <MovementTypePickerSheet
          onClose={() => setMovementTypePickerOpen(false)}
          onPick={(type) => {
            setMovementTypePickerOpen(false);
            setMovementSheetType(type);
          }}
        />
      )}

      {movementSheetType && (
        <MovementSheet
          type={movementSheetType}
          onClose={() => setMovementSheetType(null)}
          onSubmitted={() => setMovementSheetType(null)}
        />
      )}

      {consignmentFeedback && (
        <Toast
          kind={consignmentFeedback.kind}
          message={consignmentFeedback.message}
          icon={consignmentFeedback.kind === "success" ? <CheckIcon /> : undefined}
          autoCloseMs={consignmentFeedback.kind === "success" ? 2400 : 0}
          onDismiss={() => setConsignmentFeedback(null)}
        />
      )}
    </main>
  );
}

// =====================================================================
// SaleHeaderActions. Encapsulates the right-hand side of the header:
// movement button, last-sale chip and consignments shortcut.
//
// On narrow viewports (<640px) we hide Resumen/Historial/Clientes/Admin
// inside a "Más" menu (MoreHorizontal icon). The short actions remain
// inline because they are direct follow-ups from the checkout.
// =====================================================================
function SaleHeaderActions({
  onMovement,
  onVerUltima,
  lastSale,
  wrap = true,
}: {
  onMovement: () => void;
  onVerUltima: () => void;
  lastSale: { status: "active" | "voided" } | null;
  // Desktop (PageHeader) has room to wrap if needed; the compact mobile
  // instance (next to the category carousel) forces a single line instead,
  // so it never grows in height. See headerActionsCompact in SaleView.
  wrap?: boolean;
}) {
  return (
    <nav
      aria-label="Acciones de venta"
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-1)",
        flexWrap: wrap ? "wrap" : "nowrap",
      }}
    >
      <IconButton
        icon={<MoneyCircleIcon />}
        label="Registrar movimiento"
        onClick={onMovement}
        size="md"
        variant="outline"
      />

      <IconButton
        icon={<ConsignmentIcon />}
        label="Ver consignaciones"
        to="/consignaciones"
        size="md"
        variant="outline"
      />

      {lastSale && (
        <IconButton
          icon={<ReceiptIcon />}
          label={lastSale.status === "voided" ? "Ver última venta anulada" : "Ver última venta"}
          onClick={onVerUltima}
          size="md"
          variant="outline"
          badge={lastSale.status === "voided" ? <span className="last-sale-status-dot" /> : undefined}
        />
      )}

    </nav>
  );
}

// =====================================================================
// CategoryChips. Thin wrapper around the system Chip (filter / lg) so the
// active state stays in component state (not in CSS). The first chip is
// "Todos" and carries an optional LayoutGrid icon (kept simple).
// =====================================================================
function CategoryChips({
  categories,
  selected,
  onSelect,
}: {
  categories: Array<{ id: number; name: string; iconKey: ProductVisualKey }>;
  selected: number | null;
  onSelect: (c: number | null) => void;
}) {
  return (
    <div
      role="tablist"
      aria-label="Categorías"
      className="category-carousel"
    >
      <Chip
        variant="filter"
        size="lg"
        active={selected === null}
        onClick={() => onSelect(null)}
        icon={<AllCategoriesGlyph width={21} height={21} />}
        className="category-icon-tab catalog-accent-all"
        aria-label="Todos los productos"
        title="Todos"
      >
        <span className="sr-only">Todos</span>
      </Chip>
      {categories.map((c) => (
        <Chip
          key={c.id}
          variant="filter"
          size="lg"
          active={selected === c.id}
          onClick={() => onSelect(c.id)}
          icon={<CategoryGlyph name={c.name} iconKey={c.iconKey} width={22} height={22} />}
          className={`category-icon-tab ${categoryAccentClassName(c.name, c.id)}`}
          aria-label={c.name}
          title={c.name}
        >
          <span className="sr-only">{c.name}</span>
        </Chip>
      ))}
    </div>
  );
}

// =====================================================================
// SalePanel. Bottom-fixed panel. Refactored to use PaymentMethodButton
// for each method (no more inline color helpers), and to use Chip for
// the selected-customer chip. Layout per DESIGN §9.8.
// =====================================================================
function SalePanel({
  subtotalCents,
  globalDiscountCents,
  globalDiscountPct,
  previews,
  onPickMethod,
  paymentMethods,
  confirmingId,
  loading,
  selectedCustomer,
  consignmentMode,
  consignmentEditing,
  consignmentPaidCents,
  consignmentRefundedCents,
  onChooseCustomer,
  onSaveConsignment,
  onFinalizeConsignment,
  onEditNote,
  onEditGlobalDiscount,
  onSplitPayment,
  open,
  onOpenChange,
}: {
  subtotalCents: number;
  globalDiscountCents: number;
  globalDiscountPct: number;
  previews: ReturnType<typeof previewByMethod>;
  onPickMethod: (pm: PaymentMethod) => void;
  paymentMethods: PaymentMethod[];
  confirmingId: number | undefined;
  loading: boolean;
  selectedCustomer: { id: number; name: string } | null;
  consignmentMode: boolean;
  consignmentEditing: boolean;
  consignmentPaidCents: number;
  consignmentRefundedCents: number;
  onChooseCustomer: () => void;
  onSaveConsignment: () => void;
  onFinalizeConsignment: () => void;
  onEditNote: (localId: string) => void;
  onEditGlobalDiscount: () => void;
  onSplitPayment: () => void;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const itemCount = useSaleDraft(selectItemCount);
  const dockTotalCents = Math.max(0, subtotalCents - globalDiscountCents);
  const isMobileViewport = useMediaQuery("(max-width: 899px)");

  const contents = (
    <SalePanelContents
      subtotalCents={subtotalCents}
      globalDiscountCents={globalDiscountCents}
      globalDiscountPct={globalDiscountPct}
      previews={previews}
      onPickMethod={(method) => {
        if (!consignmentEditing || method.requiresNetReceived) onOpenChange(false);
        onPickMethod(method);
      }}
      paymentMethods={paymentMethods}
      confirmingId={confirmingId}
      loading={loading}
      selectedCustomer={selectedCustomer}
      consignmentMode={consignmentMode}
      consignmentEditing={consignmentEditing}
      consignmentPaidCents={consignmentPaidCents}
      consignmentRefundedCents={consignmentRefundedCents}
      onChooseCustomer={onChooseCustomer}
      onSaveConsignment={onSaveConsignment}
      onFinalizeConsignment={onFinalizeConsignment}
      onEditNote={onEditNote}
      onEditGlobalDiscount={() => {
        onOpenChange(false);
        onEditGlobalDiscount();
      }}
      onSplitPayment={() => {
        onOpenChange(false);
        onSplitPayment();
      }}
    />
  );

  return (
    <>
      <aside className="sale-desktop-panel" aria-label={consignmentEditing ? "Editar consignación" : consignmentMode ? "Consignación actual" : "Venta actual"}>
        {contents}
      </aside>

      <section className={`sale-mobile-dock${itemCount > 0 ? " has-items" : ""}`} aria-label="Resumen de venta">
        <div className="sale-mobile-dock__surface">
          <div>
            <span>{itemCount} {itemCount === 1 ? "unidad" : "unidades"}</span>
            {/* key forces a remount on total change, replaying the CSS pulse */}
            <strong key={dockTotalCents} className="num sale-mobile-dock__amount">{formatARS(dockTotalCents)}</strong>
          </div>
          <button type="button" onClick={() => onOpenChange(true)} disabled={itemCount === 0}>{itemCount === 0 ? "Venta vacía" : "Ver venta"}</button>
        </div>
      </section>

      <div className="mobile-only-sheet">
        <Sheet open={open && isMobileViewport} onClose={() => onOpenChange(false)} title={consignmentEditing ? "Editar consignación" : consignmentMode ? "Consignación actual" : "Venta actual"}>
          {contents}
        </Sheet>
      </div>
    </>
  );
}

function useMediaQuery(query: string) {
  const [matches, setMatches] = useState(() => window.matchMedia(query).matches);

  useEffect(() => {
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, [query]);

  return matches;
}

function SalePanelContents({
  subtotalCents,
  globalDiscountCents,
  globalDiscountPct,
  previews,
  onPickMethod,
  paymentMethods,
  confirmingId,
  loading,
  selectedCustomer,
  consignmentMode,
  consignmentEditing,
  consignmentPaidCents,
  consignmentRefundedCents,
  onChooseCustomer,
  onSaveConsignment,
  onFinalizeConsignment,
  onEditNote,
  onEditGlobalDiscount,
  onSplitPayment,
}: {
  subtotalCents: number;
  globalDiscountCents: number;
  globalDiscountPct: number;
  previews: ReturnType<typeof previewByMethod>;
  onPickMethod: (pm: PaymentMethod) => void;
  paymentMethods: PaymentMethod[];
  confirmingId: number | undefined;
  loading: boolean;
  selectedCustomer: { id: number; name: string } | null;
  consignmentMode: boolean;
  consignmentEditing: boolean;
  consignmentPaidCents: number;
  consignmentRefundedCents: number;
  onChooseCustomer: () => void;
  onSaveConsignment: () => void;
  onFinalizeConsignment: () => void;
  onEditNote: (localId: string) => void;
  onEditGlobalDiscount: () => void;
  onSplitPayment: () => void;
}) {
  const items = useSaleDraft((state) => state.items);
  const removeItem = useSaleDraft((state) => state.removeItem);
  const incrementItem = useSaleDraft((state) => state.incrementItem);
  const decrementItem = useSaleDraft((state) => state.decrementItem);
  const clear = useSaleDraft((state) => state.clear);
  const grossCents = items.reduce(
    (total, item) => total + item.qty * item.unitPriceCents,
    0
  );
  const discountCents = items.reduce(
    (total, item) => total + item.qty * item.lineDiscountCents,
    0
  );
  const totalCents = Math.max(0, subtotalCents - globalDiscountCents);
  const balanceCents = Math.max(0, totalCents - consignmentPaidCents + consignmentRefundedCents);
  const hasLineDiscount = discountCents > 0;

  if (items.length === 0) {
    return <div className="sale-panel-empty"><ShoppingBagIcon width={26} height={26} /><strong>Venta vacía</strong><span>Buscá un producto y tocá + para agregarlo.</span></div>;
  }

  return (
    <div className="sale-panel-content">
      {selectedCustomer ? (
        <Chip
          variant="tag"
          size="md"
          fullWidth
          icon={<UserIcon />}
          onClick={onChooseCustomer}
          className="sale-selected-customer"
          aria-label={`Cambiar cliente: ${selectedCustomer.name}`}
        >
          {selectedCustomer.name}
        </Chip>
      ) : consignmentMode ? (
        <Button variant="warning" fullWidth onClick={onChooseCustomer}>
          <UserIcon /> Elegir cliente obligatorio
        </Button>
      ) : null}
      <div className="sale-lines-scroll">
        {items.map((item) => (
          <Row
            key={item.localId}
            item={item}
            onIncrement={() => incrementItem(item.localId)}
            onDecrement={() => decrementItem(item.localId)}
            onRemove={() => removeItem(item.localId)}
            onEditNote={() => onEditNote(item.localId)}
          />
        ))}
      </div>
      <div className="financial-summary">
        <div><span>Total bruto</span><strong className="num">{formatARS(grossCents)}</strong></div>
        {discountCents > 0 && <div className="financial-summary__discount"><span>Descuentos por producto</span><strong className="num">−{formatARS(discountCents)}</strong></div>}
        {globalDiscountCents > 0 && <div className="financial-summary__discount"><span>Descuento general ({globalDiscountPct}%)</span><strong className="num">−{formatARS(globalDiscountCents)}</strong></div>}
        <div className="financial-summary__total"><span>Total actual</span><strong className="num">{formatARS(totalCents)}</strong></div>
      </div>
      <button className={`global-discount-button${globalDiscountPct > 0 ? " is-active" : ""}`} type="button" onClick={onEditGlobalDiscount}>{globalDiscountPct > 0 ? `Editar descuento general · ${globalDiscountPct}%` : "+ Descuento a toda la venta"}</button>
      {globalDiscountCents > 0 && <p className="manual-discount-note">El descuento del medio de pago no se acumula con el descuento general.</p>}
      {hasLineDiscount && globalDiscountCents === 0 && <p className="manual-discount-note">El descuento del medio de pago se aplica solo a los productos sin descuento propio.</p>}
      {consignmentMode && (
        <div className="sale-consignment-summary">
          <div className="sale-consignment-summary__row">
            <span>Pagado</span><strong className="num">{formatARS(consignmentPaidCents)}</strong>
          </div>
          {consignmentRefundedCents > 0 && <div className="sale-consignment-summary__row is-refund"><span>Devuelto</span><strong className="num">−{formatARS(consignmentRefundedCents)}</strong></div>}
          <div className="sale-consignment-summary__row is-balance">
            <span>Saldo pendiente</span><strong className="num">{formatARS(balanceCents)}</strong>
          </div>
        </div>
      )}
      {previews.length > 0 && (
        <div className="payment-previews">
          <span>{consignmentMode ? "Precio estimado al convertir" : "Precio según medio de pago"}</span>
          {previews.map((preview) => (
            <div key={preview.paymentMethod.id}>
              <span className="payment-preview__label">
                <span><ColorDot color={paymentMethodColorVar(preview.paymentMethod.color)} />{preview.paymentMethod.name} ({preview.paymentMethod.discountPct}%)</span>
                {hasLineDiscount && <small>Sobre {formatARS(preview.eligibleBaseCents)} sin descuento propio</small>}
              </span>
              <strong className="num">{formatARS(preview.totalChargedCents)}</strong>
            </div>
          ))}
          {consignmentMode && <small className="payment-previews__note">El descuento se aplicará cuando la consignación se convierta en venta.</small>}
        </div>
      )}
      {consignmentMode && balanceCents > 0 && (
        <div className="sale-consignment-payment-heading">
          <strong>{consignmentEditing ? "Cobrar saldo y registrar venta" : "Registrar anticipo"}</strong>
          <small>{consignmentEditing ? "Elegí cómo se resuelve el saldo pendiente. También puede quedar en Cuenta corriente." : "Opcional: podés guardar la consignación sin recibir dinero."}</small>
        </div>
      )}
      {(!consignmentMode || balanceCents > 0) && <div className="payment-grid">
        {(consignmentMode && !consignmentEditing ? paymentMethods.filter((method) => !method.requiresCustomer) : paymentMethods).map((method) => (
          <PaymentMethodButton
            key={method.id}
            name={consignmentMode ? method.name : method.discountPct > 0 ? `${method.name} (${method.discountPct}%)` : method.name}
            color={mapPaymentColor(method.color)}
            icon={paymentMethodIcon(method)}
            loading={loading && confirmingId === method.id}
            disabled={loading}
            onClick={() => onPickMethod(method)}
          />
        ))}
      </div>}
      {consignmentMode ? (
        <div className="sale-consignment-actions">
          <Button fullWidth size="lg" variant={consignmentEditing ? "outline" : "primary"} onClick={onSaveConsignment} disabled={loading || selectedCustomer == null}>
            {consignmentEditing ? "Guardar cambios" : "Guardar consignación"}
          </Button>
          {consignmentEditing && balanceCents === 0 && (
            <Button fullWidth size="lg" variant="primary" onClick={onFinalizeConsignment} disabled={loading || selectedCustomer == null}>
              Registrar venta
            </Button>
          )}
        </div>
      ) : (
        <button className="split-payment-button" type="button" onClick={onSplitPayment}><SplitPaymentIcon width={18} height={18} /> Dividir pago</button>
      )}
      {!consignmentEditing && <button className="clear-sale" type="button" onClick={clear}>Limpiar venta</button>}
    </div>
  );
}

export function LegacySalePanel({
  subtotalCents,
  previews,
  onPickMethod,
  paymentMethods,
  confirmingId,
  loading,
  selectedCustomer,
  onEditNote,
}: {
  subtotalCents: number;
  previews: ReturnType<typeof previewByMethod>;
  onPickMethod: (pm: PaymentMethod) => void;
  paymentMethods: PaymentMethod[];
  confirmingId: number | undefined;
  loading: boolean;
  selectedCustomer: { id: number; name: string } | null;
  onEditNote: (localId: string) => void;
}) {
  const items = useSaleDraft((s) => s.items);
  const removeItem = useSaleDraft((s) => s.removeItem);
  const incrementItem = useSaleDraft((s) => s.incrementItem);
  const decrementItem = useSaleDraft((s) => s.decrementItem);
  const clear = useSaleDraft((s) => s.clear);
  const visible = items.slice(0, 4);
  const more = items.length - visible.length;
  const itemCount = items.reduce((total, item) => total + item.qty, 0);
  const isCtaCteContext = selectedCustomer != null;

  // Grid layout for payment buttons. Per DESIGN §9.8:
  //   - 4 methods → 2×2
  //   - 3 methods → 2 in the top row, 1 full-width below
  //   - 2 methods → 1×2 (one row)
  const gridCols =
    paymentMethods.length === 4
      ? "1fr 1fr"
      : paymentMethods.length === 3
        ? "1fr 1fr"
        : `repeat(${Math.min(paymentMethods.length, 2)}, 1fr)`;

  return (
    <section
      role="region"
      aria-label="Venta actual"
      className="vl-sale-panel"
      style={{
        position: "fixed",
        left: 0,
        right: 0,
        bottom: 0,
        background: "var(--surface-raised)",
        borderTop: "1px solid var(--border-subtle)",
        boxShadow: "var(--shadow-md)",
        padding:
          "var(--space-3) var(--space-4) max(var(--space-3), env(safe-area-inset-bottom))",
        zIndex: 50,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: itemCount > 0 ? "var(--space-2)" : 0,
        }}
      >
        <span style={{ color: "var(--text-secondary)", fontSize: "var(--font-size-14)" }}>
          {itemCount} {itemCount === 1 ? "unidad" : "unidades"}
        </span>
        <span
          aria-live="polite"
          className="num"
          style={{ fontWeight: 700, fontSize: "var(--font-size-22)" }}
        >
          {formatARS(subtotalCents)}
        </span>
      </div>
      {itemCount > 0 && (
        <>
          {selectedCustomer && (
            <div style={{ marginBottom: "var(--space-2)" }}>
              <Chip
                variant="tag"
                size="md"
                icon={<UserIcon />}
              >
                {selectedCustomer.name}
              </Chip>
            </div>
          )}
          <div
            style={{ display: "flex", flexDirection: "column", gap: "var(--space-1)" }}
          >
            {visible.map((it) => (
              <Row
                key={it.localId}
                item={it}
                onIncrement={() => incrementItem(it.localId)}
                onDecrement={() => decrementItem(it.localId)}
                onRemove={() => removeItem(it.localId)}
                onEditNote={
                  isCtaCteContext ? () => onEditNote(it.localId) : undefined
                }
              />
            ))}
            {more > 0 && (
              <span
                style={{
                  color: "var(--text-secondary)",
                  fontSize: "var(--font-size-13)",
                }}
              >
                Ver {more} más
              </span>
            )}
          </div>
          {previews.length > 0 && (
            <div
              style={{
                marginTop: "var(--space-2)",
                padding: "var(--space-2) 0",
                borderTop: "1px solid var(--border-subtle)",
                display: "flex",
                flexDirection: "column",
                gap: 4,
              }}
            >
              <span
                style={{
                  color: "var(--text-secondary)",
                  fontSize: "var(--font-size-12)",
                  textTransform: "uppercase",
                  letterSpacing: "0.04em",
                  fontWeight: 500,
                }}
              >
                Si paga con
              </span>
              {previews.map((p) => {
                const accent = paymentMethodColorVar(p.paymentMethod.color);
                return (
                  <div
                    key={p.paymentMethod.id}
                    className="num"
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      gap: "var(--space-2)",
                      fontSize: "var(--font-size-14)",
                    }}
                  >
                    <span
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "var(--space-2)",
                        minWidth: 0,
                      }}
                    >
                      <ColorDot color={accent} />
                      <span
                        style={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {p.paymentMethod.name} · {p.paymentMethod.discountPct}% off
                      </span>
                    </span>
                    <span style={{ fontWeight: 600, flexShrink: 0 }}>
                      {formatARS(p.totalChargedCents)}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
          <div
            style={{
              marginTop: "var(--space-3)",
              display: "grid",
              gridTemplateColumns: gridCols,
              gap: "var(--space-2)",
            }}
          >
            {paymentMethods.map((pm, idx) => {
              // 3 methods, the third goes full-width on its own row.
              const isThirdFullWidth =
                paymentMethods.length === 3 && idx === 2;
              return (
                <PaymentMethodButton
                  key={pm.id}
                  name={pm.name}
                  color={mapPaymentColor(pm.color)}
                  icon={paymentMethodIcon(pm)}
                  loading={loading && confirmingId === pm.id}
                  disabled={loading}
                  fullWidth={isThirdFullWidth}
                  onClick={() => onPickMethod(pm)}
                  style={
                    isThirdFullWidth
                      ? { gridColumn: "1 / -1" }
                      : undefined
                  }
                />
              );
            })}
          </div>
          <div style={{ marginTop: "var(--space-2)" }}>
            <Button variant="ghost" size="md" onClick={clear}>
              Limpiar venta
            </Button>
          </div>
        </>
      )}
    </section>
  );
}

// Map the API's free-form `color` string into the closed set the system
// PaymentMethodButton accepts. "other" and any unknown value map to
// "charge" (cta cte / surface-inverse per DESIGN §9.1 / §9.13).
function mapPaymentColor(
  color: string
): "cash" | "transfer" | "card" | "charge" | "magenta" {
  if (color === "cash" || color === "transfer" || color === "card" || color === "magenta") {
    return color;
  }
  return "charge";
}

// Returns the CSS variable that paints a "Si paga con" row in the
// method's functional color. Per DESIGN §9.8 we only paint a small
// dot in the functional color — the row text stays in --text-primary.
function paymentMethodColorVar(color: string): string {
  if (color === "cash") return "var(--color-cash)";
  if (color === "transfer") return "var(--color-transfer)";
  if (color === "card") return "var(--color-card)";
  if (color === "magenta") return "var(--color-magenta)";
  return "var(--surface-inverse)";
}

// 16x16 icon for the "Si paga con" preview row, in the method's color.
function paymentMethodIcon(method: Pick<PaymentMethod, "icon" | "color">): React.ReactNode {
  if (method.icon === "Banknote") return <BanknoteIcon />;
  if (method.icon === "ArrowLeftRight") return <ArrowLeftRightIcon />;
  if (method.icon === "CreditCard") return <CreditCardIcon />;
  if (method.icon === "UserCircle") return <UserIcon />;
  if (method.color === "cash") return <BanknoteIcon />;
  if (method.color === "transfer") return <ArrowLeftRightIcon />;
  if (method.color === "card") return <CreditCardIcon />;
  return <NotebookPenIcon />;
}

// =====================================================================
// Row. Single sale-draft line in the panel. Refactored with better
// spacing and IconButton for the remove control.
// =====================================================================
function Row({
  item,
  onIncrement,
  onDecrement,
  onRemove,
  onEditNote,
}: {
  item: SaleDraftItem;
  onIncrement: () => void;
  onDecrement: () => void;
  onRemove: () => void;
  onEditNote?: () => void;
}) {
  const unitTotal = item.unitPriceCents - item.lineDiscountCents;
  const lineTotal = item.qty * unitTotal;
  const priceLabel = formatARS(item.unitPriceCents);
  return (
    <div className="sale-line">
      <div className="sale-line__copy">
        <span className="sale-line__name">
          {item.productNameSnapshot}
        </span>
        {item.sizeSnapshot && (
          <span className="sale-line__size">Talle {item.sizeSnapshot}</span>
        )}
        {(item.note || onEditNote) && (
          <div className="sale-line__note-row">
            {item.note && <span className="sale-line__note"><NoteIcon /> {item.note}</span>}
            {onEditNote && (
              <button
                className="sale-line__note-edit"
                type="button"
                onClick={onEditNote}
                aria-label={item.note ? `Editar observación de ${item.productNameSnapshot}` : `Agregar observación a ${item.productNameSnapshot}`}
                title={item.note ? "Editar observación" : "Agregar observación"}
              >
                {!item.note && <span>Agregar observación</span>}
                <PencilIcon />
              </button>
            )}
          </div>
        )}
      </div>
      <strong className="num sale-line__total">{formatARS(lineTotal)}</strong>
      <div className="sale-line__meta">
        <span>{priceLabel} c/u</span>
        {item.lineDiscountCents > 0 && (
          <span className="sale-line-discount">
            dto. {Math.round((item.lineDiscountCents / item.unitPriceCents) * 100)}% · −{formatARS(item.lineDiscountCents)} c/u
          </span>
        )}
      </div>
      <div className="sale-line__actions">
        <div className="sale-line__quantity" aria-label={`Cantidad de ${item.productNameSnapshot} a ${priceLabel}`}>
          <button
            type="button"
            aria-label={`Restar una unidad de ${item.productNameSnapshot} a ${priceLabel}`}
            onClick={onDecrement}
          >
            −
          </button>
          <span className="num" aria-live="polite">{item.qty}</span>
          <button
            type="button"
            aria-label={`Sumar una unidad de ${item.productNameSnapshot} a ${priceLabel}`}
            onClick={onIncrement}
          >
            +
          </button>
        </div>
        <IconButton
          icon={<TrashIcon />}
          label={`Quitar toda la línea de ${item.productNameSnapshot} a ${priceLabel}`}
          onClick={onRemove}
          size="sm"
          variant="danger"
        />
      </div>
    </div>
  );
}

// =====================================================================
// SearchField. Input with a Search icon glued to the left edge. Wraps
// the system Input so we don't lose its error / label API.
// =====================================================================
function SearchField({
  value,
  onChange,
  placeholder,
  "aria-label": ariaLabel,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
  "aria-label": string;
}) {
  return (
    <div style={{ position: "relative" }}>
      <span
        aria-hidden="true"
        style={{
          position: "absolute",
          left: "var(--space-3)",
          top: 0,
          bottom: 0,
          display: "inline-flex",
          alignItems: "center",
          color: "var(--text-secondary)",
          pointerEvents: "none",
        }}
      >
        <SearchIcon />
      </span>
      <Input
        aria-label={ariaLabel}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        style={{ paddingLeft: "calc(var(--space-3) + 16px + var(--space-2))" }}
      />
    </div>
  );
}

// =====================================================================
// Small color dot used in "Si paga con" rows. 10x10, in the method's
// functional color, never alone (always accompanied by method name +
// % + amount per DESIGN §4.1).
// =====================================================================
function ColorDot({ color }: { color: string }) {
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: 10,
        height: 10,
        borderRadius: "var(--radius-pill)",
        background: color,
        flexShrink: 0,
      }}
    />
  );
}

// =====================================================================
// Sheets (kept untouched in this phase). Behavior is preserved; only
// local imports may have been added/removed by the edits above.
// =====================================================================

function PriceSheet({
  product,
  onClose,
}: {
  product: Product | null;
  onClose: () => void;
}) {
  const [priceValue, setPriceValue] = useState("");
  const [discountValue, setDiscountValue] = useState("");
  const [sizeValue, setSizeValue] = useState("");
  const [noteValue, setNoteValue] = useState("");
  const [priceError, setPriceError] = useState<string | null>(null);
  const [discountError, setDiscountError] = useState<string | null>(null);
  const addItem = useSaleDraft((s) => s.addItem);

  const productId = product?.id ?? null;
  useEffectOnChange(productId, () => {
    setPriceValue("");
    setDiscountValue("");
    setSizeValue("");
    setNoteValue("");
    setPriceError(null);
    setDiscountError(null);
  });

  if (!product) return null;

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsed = parseArsInput(priceValue);
    if (!parsed.ok) {
      setPriceError(parsed.message);
      return;
    }
    const cents = parsed.cents;
    if (cents <= 0) {
      setPriceError("El precio tiene que ser mayor a 0");
      return;
    }
    if (getArsAmountPolicy(cents) === "blocked") {
      setPriceError("El importe supera el máximo permitido.");
      return;
    }
    const discountPct = discountValue === "" ? 0 : Number(discountValue);
    if (!Number.isInteger(discountPct) || discountPct < 0 || discountPct > 99) {
      setDiscountError("Usá 0 a 99");
      return;
    }
    const [sizeGuideIdText, ...sizeParts] = sizeValue.split("|");
    const size = sizeParts.join("|") || null;
    const selectedGuide = product.sizeGuides.find((guide) => guide.id === Number(sizeGuideIdText));
    addItem({
      productId: product.id,
      productName: product.name,
      unitPriceCents: cents,
      lineDiscountCents: Math.round((cents * discountPct) / 100),
      sizeGuideId: selectedGuide?.id ?? null,
      sizeGuideName: selectedGuide?.name ?? null,
      size,
      note: noteValue,
    });
    onClose();
  };

  return (
    <Sheet open={!!product} onClose={onClose} title={product.name}>
      <form
        onSubmit={submit}
        style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}
      >
        <div className="price-discount-row">
          <MoneyInput
            label="Precio"
            aria-label="Precio"
            placeholder="$ 0"
            value={priceValue}
            onChange={(e) => {
              setPriceValue(e.target.value);
              setPriceError(null);
            }}
            autoFocus
            error={priceError ?? undefined}
          />
          <Input
            label="Dto. %"
            aria-label="Descuento del producto"
            placeholder="0"
            value={discountValue}
            onChange={(e) => {
              setDiscountValue(e.target.value.replace(/\D/g, "").slice(0, 2));
              setDiscountError(null);
            }}
            inputMode="numeric"
            maxLength={2}
            error={discountError ?? undefined}
          />
        </div>
        <div className={`price-item-details${product.sizeGuides.some((guide) => guide.active) ? "" : " is-note-only"}`}>
          {product.sizeGuides.some((guide) => guide.active) && (
            <label className="field-control size-picker">
              <span>Talle <small>(opcional)</small></span>
              <select value={sizeValue} onChange={(event) => setSizeValue(event.target.value)}>
                <option value="">Sin especificar</option>
                {product.sizeGuides.filter((guide) => guide.active).map((guide) => (
                  <optgroup key={guide.id} label={guide.name}>
                    {guide.values.map((value) => <option key={`${guide.id}-${value}`} value={`${guide.id}|${value}`}>{value}</option>)}
                  </optgroup>
                ))}
              </select>
            </label>
          )}
          <label className="field-control price-note-field">
            <span>Observación <small>(opcional)</small></span>
            <textarea
              aria-label="Observación"
              placeholder="Ej.: Marca, modelo, color o detalle"
              value={noteValue}
              onChange={(event) => setNoteValue(event.target.value)}
              maxLength={500}
              rows={1}
            />
          </label>
        </div>
        <span className="price-sheet-help">El descuento es opcional y no se acumula con el del medio de pago.</span>
        <Button type="submit" fullWidth size="lg">
          Agregar
        </Button>
      </form>
    </Sheet>
  );
}

function GlobalDiscountSheet({
  open,
  subtotalCents,
  currentPct,
  onClose,
}: {
  open: boolean;
  subtotalCents: number;
  currentPct: number;
  onClose: () => void;
}) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const setGlobalDiscountPct = useSaleDraft((state) => state.setGlobalDiscountPct);

  useEffect(() => {
    if (open) {
      setValue(currentPct > 0 ? String(currentPct) : "");
      setError(null);
    }
  }, [open, currentPct]);

  const pct = value === "" ? 0 : Number(value);
  const discountCents = Number.isFinite(pct)
    ? Math.round((subtotalCents * pct) / 100)
    : 0;

  return (
    <Sheet open={open} onClose={onClose} title="Descuento general">
      <form
        className="global-discount-form"
        onSubmit={(event) => {
          event.preventDefault();
          if (!Number.isInteger(pct) || pct < 0 || pct > 99) {
            setError("Ingresá un porcentaje entre 0 y 99.");
            return;
          }
          setGlobalDiscountPct(pct);
          onClose();
        }}
      >
        <p>Se aplica sobre el subtotal ya descontado de los productos.</p>
        <Input
          label="Descuento %"
          aria-label="Descuento general"
          placeholder="0"
          value={value}
          onChange={(event) => {
            setValue(event.target.value.replace(/\D/g, "").slice(0, 2));
            setError(null);
          }}
          inputMode="numeric"
          maxLength={2}
          autoFocus
          error={error ?? undefined}
        />
        <div className="discount-preview">
          <span>Descuento</span><strong className="num">−{formatARS(discountCents)}</strong>
          <span>Total estimado</span><strong className="num">{formatARS(Math.max(0, subtotalCents - discountCents))}</strong>
        </div>
        <Button type="submit" fullWidth size="lg">{pct > 0 ? "Aplicar descuento" : "Quitar descuento"}</Button>
      </form>
    </Sheet>
  );
}

function SplitPaymentSheet({
  open,
  totalCents,
  paymentMethods,
  customers,
  selectedCustomerId,
  onSelectCustomer,
  createCustomer,
  submitting,
  onClose,
  onSubmit,
}: {
  open: boolean;
  totalCents: number;
  paymentMethods: PaymentMethod[];
  customers: CustomerListEntry[];
  selectedCustomerId: number | null;
  onSelectCustomer: (customerId: number | null) => void;
  createCustomer: ReturnType<typeof useCreateCustomer>;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (payments: SplitPaymentInput[]) => Promise<void>;
}) {
  const eligibleMethods = paymentMethods;
  const [amounts, setAmounts] = useState<Record<number, string>>({});
  const [netAmounts, setNetAmounts] = useState<Record<number, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [creatingCustomer, setCreatingCustomer] = useState(false);
  const [newCustomerFirstName, setNewCustomerFirstName] = useState("");
  const [newCustomerLastName, setNewCustomerLastName] = useState("");
  const [newCustomerPhone, setNewCustomerPhone] = useState("");
  const [customerCreateError, setCustomerCreateError] = useState<string | null>(null);
  const [createdCustomer, setCreatedCustomer] = useState<CustomerListEntry | null>(null);

  useEffect(() => {
    if (open) {
      setAmounts({});
      setNetAmounts({});
      setError(null);
      setCreatingCustomer(false);
      setNewCustomerFirstName("");
      setNewCustomerLastName("");
      setNewCustomerPhone("");
      setCustomerCreateError(null);
      setCreatedCustomer(null);
    }
  }, [open, totalCents]);

  const selectableCustomers =
    createdCustomer && !customers.some((customer) => customer.id === createdCustomer.id)
      ? [createdCustomer, ...customers]
      : customers;

  const rows = eligibleMethods.map((method) => {
    const amountInput = amounts[method.id] ?? "";
    const netInput = netAmounts[method.id] ?? "";
    const amountParsed = amountInput.trim() ? parseArsInput(amountInput) : null;
    const netParsed = netInput.trim() ? parseArsInput(netInput) : null;
    return {
      method,
      amountInput,
      netInput,
      amountParsed,
      netParsed,
      // This is display-only. `confirm` explicitly rejects any invalid text
      // instead of ever persisting it as zero.
      amountCents: amountParsed?.ok ? amountParsed.cents : 0,
      netReceivedCents: netParsed?.ok ? netParsed.cents : 0,
    };
  });
  const allocatedCents = rows.reduce((sum, row) => sum + row.amountCents, 0);
  const remainingCents = totalCents - allocatedCents;

  const fillRemainder = (methodId: number) => {
    const otherCents = rows.reduce(
      (sum, row) => sum + (row.method.id === methodId ? 0 : row.amountCents),
      0
    );
    const next = Math.max(0, totalCents - otherCents);
    setAmounts((current) => ({ ...current, [methodId]: inputValueFromCents(next) }));
    setError(null);
  };

  const createAndSelectCustomer = async () => {
    const firstName = newCustomerFirstName.trim();
    if (!firstName) {
      setCustomerCreateError("El nombre es obligatorio.");
      return;
    }

    try {
      const customer = await createCustomer.mutateAsync({
        firstName,
        lastName: newCustomerLastName.trim() || null,
        phone: newCustomerPhone.trim() || null,
      });
      setCreatedCustomer(customer);
      onSelectCustomer(customer.id);
      setCreatingCustomer(false);
      setCustomerCreateError(null);
      setError(null);
    } catch (cause) {
      setCustomerCreateError((cause as Error).message || "No se pudo crear el cliente.");
    }
  };

  const confirm = async () => {
    const invalidAmount = rows.find((row) => row.amountInput.trim() && !row.amountParsed?.ok);
    if (invalidAmount) {
      setError(`Revisá el importe de ${invalidAmount.method.name}: ${invalidAmount.amountParsed && !invalidAmount.amountParsed.ok ? invalidAmount.amountParsed.message : "monto inválido"}.`);
      return;
    }
    const invalidNet = rows.find((row) => row.netInput.trim() && !row.netParsed?.ok);
    if (invalidNet) {
      setError(`Revisá el neto acreditado de ${invalidNet.method.name}: ${invalidNet.netParsed && !invalidNet.netParsed.ok ? invalidNet.netParsed.message : "monto inválido"}.`);
      return;
    }
    const blocked = rows.find((row) =>
      (row.amountParsed?.ok && getArsAmountPolicy(row.amountParsed.cents) === "blocked") ||
      (row.netParsed?.ok && getArsAmountPolicy(row.netParsed.cents) === "blocked")
    );
    if (blocked) {
      setError(`El importe de ${blocked.method.name} supera el máximo permitido.`);
      return;
    }
    const selected = rows.filter((row) => row.amountCents > 0);
    if (selected.length < 2) {
      setError("Usá al menos dos medios de pago.");
      return;
    }
    if (remainingCents !== 0) {
      setError(remainingCents > 0 ? "Todavía falta distribuir parte del total." : "Los importes superan el total de la venta.");
      return;
    }
    const missingNet = selected.find(
      (row) => row.method.requiresNetReceived && row.netReceivedCents <= 0
    );
    if (missingNet) {
      setError(`Ingresá el neto acreditado de ${missingNet.method.name}.`);
      return;
    }
    if (selected.some((row) => row.method.requiresCustomer) && selectedCustomerId == null) {
      setError("Elegí el cliente que dejará el saldo a cuenta corriente.");
      return;
    }
    await onSubmit(selected.map((row) => ({
      paymentMethodId: row.method.id,
      amountCents: row.amountCents,
      ...(row.method.requiresNetReceived
        ? { netReceivedCents: row.netReceivedCents }
        : {}),
    })));
  };

  return (
    <Sheet open={open} onClose={onClose} title="Dividir pago">
      <div className="split-payment-sheet">
        <div className="split-payment-total"><span>Total a distribuir</span><strong className="num">{formatARS(totalCents)}</strong></div>
        <p className="split-payment-help">Ingresá cuánto se paga con cada medio. En un pago dividido no se aplica descuento por medio de pago.</p>
        <div className="split-payment-methods">
          {eligibleMethods.map((method) => {
            const row = rows.find((entry) => entry.method.id === method.id)!;
            return (
              <section className={`split-payment-method payment-${method.color}`} key={method.id}>
                <div className="split-payment-method__heading"><span>{paymentMethodIcon(method)}<strong>{method.name}</strong></span><button type="button" onClick={() => fillRemainder(method.id)}>Completar</button></div>
                <MoneyInput
                  aria-label={`Importe con ${method.name}`}
                  placeholder="$ 0"
                  value={amounts[method.id] ?? ""}
                  onChange={(event) => { setAmounts((current) => ({ ...current, [method.id]: event.target.value })); setError(null); }}
                />
                {method.requiresNetReceived && row.amountCents > 0 && (
                  <MoneyInput
                    label="Neto acreditado"
                    aria-label={`Neto acreditado con ${method.name}`}
                    placeholder="$ 0"
                    value={netAmounts[method.id] ?? ""}
                    onChange={(event) => { setNetAmounts((current) => ({ ...current, [method.id]: event.target.value })); setError(null); }}
                  />
                )}
                {method.requiresCustomer && row.amountCents > 0 && (
                  <div className="split-payment-customer">
                    <label className="field-control">
                      <span>Cliente de cuenta corriente</span>
                      <select value={selectedCustomerId ?? ""} onChange={(event) => { onSelectCustomer(event.target.value ? Number(event.target.value) : null); setError(null); }}>
                        <option value="">Elegir cliente…</option>
                        {selectableCustomers.map((customer) => <option key={customer.id} value={customer.id}>{fullName(customer)}</option>)}
                      </select>
                    </label>
                    {!creatingCustomer ? (
                      <button
                        className="split-payment-create-customer"
                        type="button"
                        onClick={() => {
                          setCreatingCustomer(true);
                          setCustomerCreateError(null);
                        }}
                      >
                        + Crear cliente nuevo
                      </button>
                    ) : (
                      <form
                        className="split-payment-customer-form"
                        onSubmit={(event) => {
                          event.preventDefault();
                          void createAndSelectCustomer();
                        }}
                      >
                        <strong>Nuevo cliente</strong>
                        <Input
                          aria-label="Nombre del nuevo cliente"
                          placeholder="Nombre (obligatorio)"
                          value={newCustomerFirstName}
                          onChange={(event) => {
                            setNewCustomerFirstName(event.target.value);
                            setCustomerCreateError(null);
                          }}
                          autoFocus
                          error={customerCreateError ?? undefined}
                        />
                        <div className="split-payment-customer-form__optional">
                          <Input
                            aria-label="Apellido del nuevo cliente"
                            placeholder="Apellido (opcional)"
                            value={newCustomerLastName}
                            onChange={(event) => setNewCustomerLastName(event.target.value)}
                          />
                          <Input
                            aria-label="Teléfono del nuevo cliente"
                            placeholder="Teléfono (opcional)"
                            value={newCustomerPhone}
                            onChange={(event) => setNewCustomerPhone(event.target.value)}
                          />
                        </div>
                        <div className="split-payment-customer-form__actions">
                          <Button type="button" variant="ghost" onClick={() => setCreatingCustomer(false)}>Cancelar</Button>
                          <Button type="submit" disabled={createCustomer.isPending}>
                            {createCustomer.isPending ? "Creando…" : "Crear y elegir"}
                          </Button>
                        </div>
                      </form>
                    )}
                  </div>
                )}
              </section>
            );
          })}
        </div>
        <small className="split-payment-customer-note">La parte asignada a cuenta corriente queda como deuda del cliente y no cuenta como dinero ingresado.</small>
        <div className={`split-payment-balance${remainingCents === 0 ? " is-complete" : remainingCents < 0 ? " is-over" : ""}`}><span>{remainingCents === 0 ? "Distribución completa" : remainingCents > 0 ? "Falta distribuir" : "Importe excedido"}</span><strong className="num">{formatARS(Math.abs(remainingCents))}</strong></div>
        {error && <p className="form-error">{error}</p>}
        <Button fullWidth size="lg" onClick={confirm} disabled={submitting || totalCents <= 0 || remainingCents !== 0}>{submitting ? "Registrando…" : "Confirmar pago dividido"}</Button>
      </div>
    </Sheet>
  );
}

function ConsignmentPaymentSheet({
  method,
  remainingCents,
  onClose,
  onSubmit,
}: {
  method: PaymentMethod;
  remainingCents: number;
  onClose: () => void;
  onSubmit: (payment: ConsignmentPaymentInput) => void;
}) {
  const [amount, setAmount] = useState(remainingCents > 0 ? inputValueFromCents(remainingCents) : "");
  const [net, setNet] = useState("");
  const [error, setError] = useState<string | null>(null);

  const submit = () => {
    const parsedAmount = parseArsInput(amount);
    if (!parsedAmount.ok) {
      setError(parsedAmount.message);
      return;
    }
    const amountCents = parsedAmount.cents;
    if (amountCents <= 0) {
      setError("El importe tiene que ser mayor a 0.");
      return;
    }
    if (getArsAmountPolicy(amountCents) === "blocked") {
      setError("El importe supera el máximo permitido.");
      return;
    }
    if (remainingCents > 0 && amountCents > remainingCents) {
      setError("El anticipo no puede superar el saldo pendiente.");
      return;
    }
    const parsedNet = method.requiresNetReceived && net.trim() ? parseArsInput(net) : null;
    if (parsedNet && !parsedNet.ok) {
      setError(parsedNet.message);
      return;
    }
    if (parsedNet?.ok && getArsAmountPolicy(parsedNet.cents) === "blocked") {
      setError("El neto acreditado supera el máximo permitido.");
      return;
    }
    const netReceivedCents = parsedNet?.ok ? parsedNet.cents : undefined;
    if (method.requiresNetReceived && (!netReceivedCents || netReceivedCents <= 0)) {
      onSubmit({ paymentMethodId: method.id, amountCents, skipNetReceived: true });
      return;
    }
    onSubmit({ paymentMethodId: method.id, amountCents, ...(netReceivedCents ? { netReceivedCents } : {}) });
  };

  return (
    <Sheet open onClose={onClose} title={`Registrar anticipo · ${method.name}`}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <p style={{ margin: 0, color: "var(--text-secondary)" }}>Podés registrar una entrega parcial ahora y completar la consignación más adelante.</p>
        <MoneyInput label="Importe recibido" value={amount} onChange={(event) => { setAmount(event.target.value); setError(null); }} autoFocus />
        {method.requiresNetReceived && <MoneyInput label="Neto acreditado" value={net} onChange={(event) => { setNet(event.target.value); setError(null); }} />}
        {error && <p className="form-error">{error}</p>}
        <Button fullWidth size="lg" onClick={submit}>Agregar anticipo</Button>
        <Button fullWidth variant="ghost" onClick={onClose}>Cancelar</Button>
      </div>
    </Sheet>
  );
}

function ConsignmentSettlementSheet({
  method,
  remainingCents,
  pending,
  onClose,
  onSubmit,
}: {
  method: PaymentMethod;
  remainingCents: number;
  pending: boolean;
  onClose: () => void;
  onSubmit: (payment: ConsignmentPaymentInput) => Promise<void>;
}) {
  const [net, setNet] = useState("");
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    const parsedNet = method.requiresNetReceived && net.trim() ? parseArsInput(net) : null;
    if (parsedNet && !parsedNet.ok) {
      setError(parsedNet.message);
      return;
    }
    if (parsedNet?.ok && getArsAmountPolicy(parsedNet.cents) === "blocked") {
      setError("El neto acreditado supera el máximo permitido.");
      return;
    }
    const netReceivedCents = parsedNet?.ok ? parsedNet.cents : undefined;
    await onSubmit({
      paymentMethodId: method.id,
      amountCents: remainingCents,
      ...(method.requiresNetReceived && (!netReceivedCents || netReceivedCents <= 0) ? { skipNetReceived: true } : {}),
      ...(netReceivedCents ? { netReceivedCents } : {}),
    });
  };

  return (
    <Sheet open onClose={onClose} title={`Cobrar saldo · ${method.name}`}>
      <div className="consignment-settlement-sheet">
        <div className="consignment-settlement-sheet__amount"><span>Saldo a cobrar</span><strong className="num">{formatARS(remainingCents)}</strong></div>
        <p>Registrá el neto acreditado para completar la venta. Si todavía no lo conocés, podés omitirlo.</p>
        <MoneyInput label="Neto acreditado" value={net} onChange={(event) => { setNet(event.target.value); setError(null); }} autoFocus />
        {error && <p className="form-error">{error}</p>}
        <Button fullWidth size="lg" onClick={() => void submit()} disabled={pending}>{pending ? "Registrando…" : "Registrar venta"}</Button>
        <Button fullWidth variant="ghost" onClick={onClose} disabled={pending}>Cancelar</Button>
      </div>
    </Sheet>
  );
}

function NetReceivedSheet({
  method,
  onClose,
  onSubmit,
}: {
  method: PaymentMethod;
  onClose: () => void;
  onSubmit: (mode: "confirm" | "skip", value?: number) => Promise<void>;
}) {
  const [value, setValue] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const confirm = async () => {
    const parsed = parseArsInput(value);
    if (!parsed.ok) {
      setError(parsed.message);
      return;
    }
    const cents = parsed.cents;
    if (cents <= 0) {
      setError("El monto tiene que ser mayor a 0");
      return;
    }
    if (getArsAmountPolicy(cents) === "blocked") {
      setError("El importe supera el máximo permitido.");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit("confirm", cents);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Sheet open onClose={onClose} title={`${method.name} · neto recibido`}>
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <p style={{ margin: 0, color: "var(--text-secondary)" }}>
          Anotá cuánto entró realmente a la cuenta. Si querés, podés omitir por
          ahora y cargarlo después desde el historial de ventas.
        </p>
        <MoneyInput
          aria-label="Neto recibido"
          placeholder="Neto recibido"
          value={value}
          onChange={(e) => {
            setValue(e.target.value);
            setError(null);
          }}
          autoFocus
          error={error ?? undefined}
        />
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
          <Button
            variant="primary"
            fullWidth
            size="lg"
            onClick={confirm}
            disabled={submitting}
          >
            {submitting ? "Confirmando..." : "Confirmar"}
          </Button>
          <Button
            variant="outline"
            fullWidth
            size="lg"
            onClick={() => onSubmit("skip")}
            disabled={submitting}
          >
            Omitir por ahora
          </Button>
          <Button variant="ghost" fullWidth size="md" onClick={onClose} disabled={submitting}>
            Cancelar
          </Button>
        </div>
        {method.discountPct > 0 && (
          <p
            style={{
              margin: 0,
              fontSize: "var(--font-size-12)",
              color: "var(--text-secondary)",
            }}
          >
            El descuento configurado de {method.discountPct}% se aplicará automáticamente a los productos elegibles.
          </p>
        )}
      </div>
    </Sheet>
  );
}

function SuccessModal({
  total,
  methodName,
  onVerDetalle,
  onCerrar,
}: {
  total: number;
  methodName: string;
  onVerDetalle: () => void;
  onCerrar: () => void;
}) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 200,
        padding: "var(--space-4)",
      }}
    >
      <div
        style={{
          background: "var(--surface-raised)",
          borderRadius: "var(--radius-lg)",
          boxShadow: "var(--shadow-lg)",
          width: "100%",
          maxWidth: 380,
          padding: "var(--space-5)",
          position: "relative",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 4,
            background: "var(--color-cash)",
            borderTopLeftRadius: "var(--radius-lg)",
            borderTopRightRadius: "var(--radius-lg)",
          }}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: "var(--space-3)",
            paddingTop: "var(--space-3)",
          }}
        >
          <div
            style={{
              width: 48,
              height: 48,
              borderRadius: "var(--radius-pill)",
              background: "var(--color-cash)",
              color: "var(--text-inverse)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 24,
            }}
          >
            ✓
          </div>
          <p style={{ margin: 0, color: "var(--text-secondary)" }}>
            Venta registrada · {methodName}
          </p>
          <p
            className="num"
            style={{ margin: 0, fontWeight: 700, fontSize: "var(--font-size-36)" }}
          >
            {formatARS(total)}
          </p>
          <div
            style={{ display: "flex", gap: "var(--space-3)", marginTop: "var(--space-2)" }}
          >
            <Button variant="primary" onClick={onCerrar}>
              Cerrar
            </Button>
            <Button
              variant="ghost"
              onClick={onVerDetalle}
            >
              Ver detalle
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function useEffectOnChange<T>(value: T, effect: () => void) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => effect(), [value]);
}

// =====================================================================
// Customer picker for cta cte sales. Search-as-you-type with a debounce;
// the picker returns the active customers list when the search has fewer
// than 3 chars (so just opening it shows useful defaults). An inline
// 'Crear cliente' flow is exposed via the "+ Crear cliente" button at
// the bottom of the results.
// =====================================================================
function ChooseCustomerSheet({
  open,
  onClose,
  onPick,
  createCustomer,
}: {
  open: boolean;
  onClose: () => void;
  onPick: (customerId: number) => void;
  createCustomer: ReturnType<typeof useCreateCustomer>;
}) {
  const [rawQuery, setRawQuery] = useState("");
  const [debounced, setDebounced] = useState("");

  useEffect(() => {
    const t = setTimeout(() => setDebounced(rawQuery), 250);
    return () => clearTimeout(t);
  }, [rawQuery]);

  const customersQ = useCustomers(
    debounced.length >= 3 ? { q: debounced, limit: 50 } : { limit: 50 }
  );
  const customers: CustomerListEntry[] = customersQ.data ?? [];

  // Inline creation
  const [creating, setCreating] = useState(false);
  const [newFirst, setNewFirst] = useState("");
  const [newLast, setNewLast] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newNote, setNewNote] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);

  const submitCreate = async () => {
    if (newFirst.trim().length === 0) {
      setCreateError("El nombre es obligatorio");
      return;
    }
    try {
      const c = await createCustomer.mutateAsync({
        firstName: newFirst.trim(),
        lastName: newLast.trim() || null,
        phone: newPhone.trim() || null,
        email: newEmail.trim() || null,
        note: newNote.trim() || null,
      });
      onPick(c.id);
    } catch (e) {
      setCreateError((e as Error).message);
    }
  };

  return (
    <Sheet open={open} onClose={onClose} title="Elegir cliente">
      {!creating ? (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          <input
            type="search"
            placeholder="Buscar (nombre, teléfono, email)"
            value={rawQuery}
            onChange={(e) => setRawQuery(e.target.value)}
            style={{
              padding: "10px 14px",
              border: "1px solid var(--border-default)",
              borderRadius: "var(--radius-md)",
              fontSize: "var(--font-size-14)",
              background: "var(--surface-base)",
              color: "var(--text-primary)",
            }}
          />
          <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            {customersQ.isLoading && (
              <span
                style={{
                  color: "var(--text-secondary)",
                  fontSize: "var(--font-size-13)",
                }}
              >
                Cargando...
              </span>
            )}
            {!customersQ.isLoading && customers.length === 0 && (
              <span
                style={{
                  color: "var(--text-secondary)",
                  fontSize: "var(--font-size-13)",
                }}
              >
                {rawQuery.length >= 3
                  ? `Sin coincidencias para "${rawQuery}".`
                  : "No hay clientes activos todavía."}
              </span>
            )}
            {customers.map((c) => (
              <button
                key={c.id}
                type="button"
                onClick={() => onPick(c.id)}
                style={{
                  textAlign: "left",
                  padding: "var(--space-3)",
                  borderRadius: "var(--radius-md)",
                  border: "1px solid var(--border-default)",
                  background: "var(--surface-base)",
                  cursor: "pointer",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                }}
              >
                <strong>{fullName(c)}</strong>
                <span
                  style={{
                    fontSize: "var(--font-size-13)",
                    color: "var(--text-secondary)",
                  }}
                >
                  {c.phone ?? (c.email ?? "—")}
                </span>
              </button>
            ))}
          </div>
          <Button variant="outline" fullWidth onClick={() => setCreating(true)}>
            + Crear cliente
          </Button>
        </div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
          <Input
            aria-label="Nombre"
            placeholder="Nombre (obligatorio)"
            value={newFirst}
            onChange={(e) => {
              setNewFirst(e.target.value);
              setCreateError(null);
            }}
            autoFocus
            error={createError ?? undefined}
          />
          <Input
            aria-label="Apellido"
            placeholder="Apellido (opcional)"
            value={newLast}
            onChange={(e) => setNewLast(e.target.value)}
          />
          <Input
            aria-label="Teléfono"
            placeholder="Teléfono (opcional)"
            value={newPhone}
            onChange={(e) => setNewPhone(e.target.value)}
          />
          <Input
            aria-label="Email"
            placeholder="Email (opcional)"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
          />
          <Input
            aria-label="Nota"
            placeholder="Nota (opcional)"
            value={newNote}
            onChange={(e) => setNewNote(e.target.value)}
          />
          <Button
            variant="primary"
            fullWidth
            size="lg"
            onClick={submitCreate}
            disabled={createCustomer.isPending}
          >
            {createCustomer.isPending ? "Guardando..." : "Crear y elegir"}
          </Button>
          <Button
            variant="ghost"
            fullWidth
            onClick={() => {
              setCreating(false);
              setCreateError(null);
            }}
          >
            Volver al buscador
          </Button>
        </div>
      )}
    </Sheet>
  );
}

// =====================================================================
// Per-line observation sheet. Free-text multilínea, saved against a single
// draft item. The observation is metadata of the line and has no impact on
// totals — see DATA-MODEL.md.
// =====================================================================
function LineNoteSheet({
  open,
  item,
  onClose,
  onSave,
}: {
  open: boolean;
  item: SaleDraftItem | null;
  onClose: () => void;
  onSave: (note: string | null) => void;
}) {
  const [value, setValue] = useState("");

  useEffect(() => {
    if (open) setValue(item?.note ?? "");
  }, [open, item]);

  return (
    <Sheet open={open} onClose={onClose} title="Observación por línea">
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-4)" }}>
        <p
          style={{
            margin: 0,
            color: "var(--text-secondary)",
            fontSize: "var(--font-size-13)",
          }}
        >
          {item?.productNameSnapshot ?? ""}
        </p>
        <textarea
          aria-label="Observación"
          placeholder="Ej.: Marca, modelo, color o detalle"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          maxLength={500}
          rows={4}
          style={{
            padding: "10px 14px",
            border: "1px solid var(--border-default)",
            borderRadius: "var(--radius-md)",
            fontSize: "var(--font-size-14)",
            background: "var(--surface-base)",
            color: "var(--text-primary)",
            resize: "vertical",
            fontFamily: "inherit",
          }}
          autoFocus
        />
        <div style={{ display: "flex", gap: "var(--space-2)" }}>
          {item?.note && (
            <Button
              variant="ghost"
              onClick={() => {
                onSave(null);
              }}
            >
              Quitar
            </Button>
          )}
          <Button
            variant="primary"
            fullWidth
            onClick={() => onSave(value.trim() ? value : null)}
          >
            Guardar
          </Button>
        </div>
      </div>
    </Sheet>
  );
}

// =====================================================================
// Manual movement flow. DESIGN §6.9: a button in the header opens the
// type picker; choosing a type opens the detail sheet (monto, concepto,
// medio opcional, nota opcional, Guardar).
//
// We render the type picker and the detail sheet as two separate
// components so closing one always returns to a clean state — never
// half-filled with the previous type's value still selected.
// =====================================================================

function MovementTypePickerSheet({
  onClose,
  onPick,
}: {
  onClose: () => void;
  onPick: (type: "income" | "expense") => void;
}) {
  return (
    <Sheet open onClose={onClose} title="Registrar movimiento">
      <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-2)" }}>
        <button
          type="button"
          onClick={() => onPick("income")}
          style={movementTypeButtonStyle("var(--color-cash)")}
        >
          <span style={{ fontWeight: 600, fontSize: "var(--font-size-15)" }}>
            Ingreso
          </span>
        </button>
        <button
          type="button"
          onClick={() => onPick("expense")}
          style={movementTypeButtonStyle("var(--color-danger)")}
        >
          <span style={{ fontWeight: 600, fontSize: "var(--font-size-15)" }}>
            Egreso
          </span>
        </button>
      </div>
    </Sheet>
  );
}

function movementTypeButtonStyle(accent: string): React.CSSProperties {
  return {
    background: "var(--surface-base)",
    color: "var(--text-primary)",
    border: `1px solid ${accent}`,
    borderRadius: "var(--radius-md)",
    padding: "var(--space-3)",
    minHeight: 56,
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };
}

function MovementSheet({
  type,
  onClose,
  onSubmitted,
}: {
  type: "income" | "expense";
  onClose: () => void;
  onSubmitted: () => void;
}) {
  const [amount, setAmount] = useState("");
  const [conceptId, setConceptId] = useState<number | null>(null);
  const [conceptQuery, setConceptQuery] = useState("");
  const [paymentMethodId, setPaymentMethodId] = useState<number | null>(null);
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [creatingConcept, setCreatingConcept] = useState(false);
  const [pendingConfirmation, setPendingConfirmation] = useState<{
    body: {
      type: "income" | "expense";
      amountCents: number;
      conceptId: number;
      paymentMethodId: number | null;
      note: string | null;
    };
    token: string;
    amountCents: number;
  } | null>(null);

  const allConceptsQ = useMovementConcepts(type);
  const recentQ = useRecentConcepts(type, 5);
  const createConcept = useCreateMovementConcept();
  const createMovement = useCreateMovement();
  const paymentMethodsQ = usePaymentMethodsForSale();

  const onSubmit = async () => {
    setError(null);
    const parsed = parseArsInput(amount);
    if (!parsed.ok) {
      setError(parsed.message);
      return;
    }
    const cents = parsed.cents;
    if (cents <= 0) {
      setError("El monto tiene que ser mayor a 0");
      return;
    }
    if (getArsAmountPolicy(cents) === "blocked") {
      setError("El importe supera el máximo permitido");
      return;
    }
    if (conceptId == null) {
      setError("Elegí un concepto");
      return;
    }
    const body = {
      type,
      amountCents: cents,
      conceptId,
      paymentMethodId,
      note: note.trim() || null,
    };
    setSubmitting(true);
    try {
      await createMovement.mutateAsync(body);
      onSubmitted();
    } catch (e) {
      const confirmation = getMoneyConfirmation(e);
      if (confirmation) {
        setPendingConfirmation({ body, token: confirmation.token, amountCents: confirmation.amountCents ?? cents });
      } else {
        setError((e as Error).message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const confirmUnusualAmount = async () => {
    if (!pendingConfirmation) return;
    setError(null);
    setSubmitting(true);
    try {
      await createMovement.mutateAsync({
        ...pendingConfirmation.body,
        moneyConfirmationToken: pendingConfirmation.token,
      });
      onSubmitted();
    } catch (e) {
      const confirmation = getMoneyConfirmation(e);
      if (confirmation) {
        setPendingConfirmation((current) => current && {
          ...current,
          token: confirmation.token,
          amountCents: confirmation.amountCents ?? current.amountCents,
        });
      } else {
        setPendingConfirmation(null);
        setError((e as Error).message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const onCreateConcept = async () => {
    const name = conceptQuery.trim();
    if (name.length === 0) return;
    try {
      const c = await createConcept.mutateAsync({ type, name });
      setConceptId(c.id);
      setConceptQuery("");
      setCreatingConcept(false);
    } catch (e) {
      setError((e as Error).message);
    }
  };

  const concepts = allConceptsQ.data ?? [];
  const recentIds = new Map((recentQ.data ?? []).map((concept, index) => [concept.id, index]));
  const filteredConcepts =
    conceptQuery.trim().length > 0
      ? concepts.filter((c) =>
          c.name.toLowerCase().includes(conceptQuery.toLowerCase())
        )
      : [...concepts].sort((a, b) => {
          const recentOrder = (recentIds.get(a.id) ?? 999) - (recentIds.get(b.id) ?? 999);
          return recentOrder || a.name.localeCompare(b.name, "es-AR");
        });

  const exactMatch = concepts.find(
    (c) => c.name.toLowerCase() === conceptQuery.trim().toLowerCase()
  );
  const selectedConcept = concepts.find((concept) => concept.id === conceptId) ?? null;

  return (
    <Sheet open onClose={onClose} title={type === "income" ? "Nuevo ingreso" : "Nuevo egreso"}>
      {pendingConfirmation ? (
        <MoneyConfirmationPanel
          amountCents={pendingConfirmation.amountCents}
          actionLabel={type === "income" ? "Confirmar ingreso" : "Confirmar egreso"}
          pending={submitting}
          onConfirm={confirmUnusualAmount}
          onCancel={() => setPendingConfirmation(null)}
        />
      ) : <div style={{ display: "flex", flexDirection: "column", gap: "var(--space-3)" }}>
        <MoneyInput
          aria-label="Monto"
          placeholder="Monto"
          value={amount}
          onChange={(e) => {
            setAmount(e.target.value);
            setError(null);
          }}
          autoFocus
          error={error ?? undefined}
        />

        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span
            style={{
              color: "var(--text-secondary)",
              fontSize: "var(--font-size-12)",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            Concepto
          </span>
          {!creatingConcept ? (
            <>
              <input
                type="search"
                placeholder="Buscar o crear un concepto"
                value={conceptQuery}
                onChange={(e) => {
                  setConceptQuery(e.target.value);
                  setConceptId(null);
                }}
                style={conceptSearchStyle}
              />
              {selectedConcept && (
                <div className="concept-selected" role="status">
                  <CheckIcon />
                  <span><small>Concepto seleccionado</small><strong>{selectedConcept.name}</strong></span>
                  <button type="button" onClick={() => setConceptId(null)}>Cambiar</button>
                </div>
              )}
              {(filteredConcepts.length > 0 || conceptQuery.trim().length > 0) && !selectedConcept && (
                <div
                  className="concept-options"
                  style={{
                    maxHeight: 180,
                    overflowY: "auto",
                    border: "1px solid var(--border-subtle)",
                    borderRadius: "var(--radius-md)",
                  }}
                >
                  {filteredConcepts.map((c) => (
                    <button
                      key={c.id}
                      type="button"
                      onClick={() => {
                        setConceptId(c.id);
                        setConceptQuery("");
                      }}
                      style={conceptOptionStyle(conceptId === c.id)}
                    >
                      {c.name}
                    </button>
                  ))}
                  {conceptQuery.trim().length > 0 && !exactMatch && (
                    <button
                      type="button"
                      onClick={() => setCreatingConcept(true)}
                      style={conceptOptionStyle(false)}
                    >
                      + Crear "{conceptQuery.trim()}"
                    </button>
                  )}
                </div>
              )}
            </>
          ) : (
            <div
              style={{
                padding: "var(--space-3)",
                border: "1px solid var(--border-subtle)",
                borderRadius: "var(--radius-md)",
                display: "flex",
                flexDirection: "column",
                gap: "var(--space-2)",
              }}
            >
              <span style={{ fontSize: "var(--font-size-13)" }}>
                ¿Crear el concepto <strong>"{conceptQuery.trim()}"</strong>?
              </span>
              <Button
                variant="primary"
                size="md"
                onClick={onCreateConcept}
                disabled={createConcept.isPending}
              >
                {createConcept.isPending ? "Creando..." : "Sí, crear"}
              </Button>
              <Button
                variant="ghost"
                size="md"
                onClick={() => setCreatingConcept(false)}
              >
                Cancelar
              </Button>
            </div>
          )}
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
          <span
            style={{
              color: "var(--text-secondary)",
              fontSize: "var(--font-size-12)",
              textTransform: "uppercase",
              letterSpacing: "0.04em",
            }}
          >
            Medio de pago (opcional)
          </span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            <ChipButton
              active={paymentMethodId == null}
              onClick={() => setPaymentMethodId(null)}
            >
              —
            </ChipButton>
            {(paymentMethodsQ.data ?? [])
              .filter((pm) => pm.requiresCustomer !== true)
              .map((pm) => (
                <ChipButton
                  key={pm.id}
                  active={paymentMethodId === pm.id}
                  onClick={() => setPaymentMethodId(pm.id)}
                >
                  {pm.name}
                </ChipButton>
              ))}
          </div>
        </div>

        <Input
          aria-label="Nota"
          placeholder="Nota (opcional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />

        <Button
          variant="primary"
          fullWidth
          size="lg"
          onClick={onSubmit}
          disabled={submitting}
        >
          {submitting
            ? "Guardando..."
            : type === "income"
              ? "Guardar ingreso"
              : "Guardar egreso"}
        </Button>
      </div>}
    </Sheet>
  );
}

function getMoneyConfirmation(error: unknown): { token: string; amountCents?: number } | null {
  if (!(error instanceof ApiError) || error.status !== 422 || !error.body || typeof error.body !== "object") return null;
  const body = error.body as {
    error?: unknown;
    confirmationToken?: unknown;
    warnings?: Array<{ path?: unknown; amountCents?: unknown }>;
  };
  if (body.error !== "money_confirmation_required" || typeof body.confirmationToken !== "string") return null;
  const amounts = (body.warnings ?? [])
    .map((warning) => warning.amountCents)
    .filter((amount): amount is number => typeof amount === "number" && Number.isSafeInteger(amount));
  return {
    token: body.confirmationToken,
    amountCents: amounts.length > 0 ? Math.max(...amounts) : undefined,
  };
}

const conceptSearchStyle: React.CSSProperties = {
  padding: "8px 12px",
  border: "1px solid var(--border-default)",
  borderRadius: "var(--radius-md)",
  fontSize: "var(--font-size-14)",
  background: "var(--surface-base)",
  color: "var(--text-primary)",
};

function conceptOptionStyle(active: boolean): React.CSSProperties {
  return {
    width: "100%",
    textAlign: "left",
    padding: "10px 12px",
    fontSize: "var(--font-size-14)",
    background: active ? "var(--surface-sunken)" : "var(--surface-base)",
    color: "var(--text-primary)",
    border: "none",
    borderBottom: "1px solid var(--border-subtle)",
    cursor: "pointer",
  };
}

function ChipButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: "6px 12px",
        borderRadius: "var(--radius-pill)",
        border: "1px solid var(--border-default)",
        background: active ? "var(--text-primary)" : "var(--surface-base)",
        color: active ? "var(--text-inverse)" : "var(--text-primary)",
        cursor: "pointer",
        fontSize: "var(--font-size-13)",
        fontWeight: 500,
      }}
    >
      {children}
    </button>
  );
}

// =====================================================================
// Icons. Inline SVGs in the Lucide visual line. Centralized here so
// they're easy to swap when the project moves to the real package.
// =====================================================================

function ArrowDownUpIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M4 2v10m0 0l-2-2m2 2l2-2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <path d="M12 14V4m0 0l-2 2m2-2l2 2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function UserIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <circle cx="8" cy="6" r="2.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M2.5 14c.7-2.7 2.7-4.5 5.5-4.5s4.8 1.8 5.5 4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function BanknoteIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <circle cx="8" cy="8" r="1.8" stroke="currentColor" strokeWidth="1.5" />
      <path d="M3.5 5.5L5 7.5M12.5 5.5L11 7.5M3.5 10.5L5 8.5M12.5 10.5L11 8.5" stroke="currentColor" strokeWidth="1.2" strokeLinecap="round" />
    </svg>
  );
}

function ArrowLeftRightIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2 5h11M10 2l3 3-3 3M14 11H3M6 14l-3-3 3-3" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CreditCardIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <rect x="1.5" y="3.5" width="13" height="9" rx="1.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M1.5 6.5h13M3.5 10h2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function NotebookPenIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M3 2.5h7.5a2 2 0 0 1 2 2V13a.5.5 0 0 1-.5.5H4a.5.5 0 0 1-.5-.5v-10a.5.5 0 0 1 .5-.5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M3 5.5h9.5" stroke="currentColor" strokeWidth="1.5" />
      <path d="M11.5 9.5l1.5 1.5-3 1.5 1.5-3z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
    </svg>
  );
}

function NoteIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2 2.5h8L13 5.5v8H2v-11z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M5 8h6M5 11h4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
    </svg>
  );
}

function PencilIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2 14l1-3 8-8 3 3-8 8-3 .5z" stroke="currentColor" strokeWidth="1.5" strokeLinejoin="round" />
      <path d="M10 3l3 3" stroke="currentColor" strokeWidth="1.5" />
    </svg>
  );
}
